import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { useAdmin } from '../admin-context';
import axios from 'axios';
import { apiUrl } from '../../../utils/config';
import MediaPicker from '../MediaPicker';

import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";




/* ═══════════════════════════════════════════
   SHARED FIELD HELPERS
═══════════════════════════════════════════ */
function FieldInput({ label, value, onChange, placeholder }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
      <input
        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-indigo-400 transition"
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
      />
    </label>
  );
}

function FieldTextarea({ label, value, onChange, rows = 4 }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
      <textarea
        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-indigo-400 transition resize-vertical"
        value={value ?? ''}
        onChange={onChange}
        rows={rows}
      />
    </label>
  );
}

function FieldSelect({ label, value, onChange, options }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
      <select
        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-indigo-400 transition"
        value={value}
        onChange={onChange}
      >
        {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
    </label>
  );
}

function FieldImagePicker({ label, value, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <label className="flex flex-col gap-1">
      {label && <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-left transition hover:border-orange-400"
      >
        {value
          ? <img src={value} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0 border" />
          : <div className="w-8 h-8 rounded bg-slate-100 flex-shrink-0" />
        }
        <span className={`truncate text-xs flex-1 ${value ? 'text-slate-500' : 'text-slate-400'}`}>
          {value ? value : 'Pick from Media Library…'}
        </span>
        {value && (
          <span
            role="button"
            onClick={e => { e.stopPropagation(); onPick(''); }}
            className="text-slate-300 hover:text-red-400 text-base leading-none flex-shrink-0"
            title="Clear"
          >✕</span>
        )}
      </button>
      {open && createPortal(
        <MediaPicker
          onSelect={({ url }) => { onPick(url); setOpen(false); }}
          onClose={() => setOpen(false)}
        />,
        document.body
      )}
    </label>
  );
}

/* ═══════════════════════════════════════════
   SECTION EDITOR
═══════════════════════════════════════════ */
function SectionEditor({ section, updateSection, deleteSection, moveSection, isFirst, isLast }) {
  const update = (k, v) => updateSection(section.id, { ...section.content, [k]: v });

  const updateArrayItem = (arr, arrKey, idx, k, v) => {
    const newArr = [...arr];
    newArr[idx] = { ...newArr[idx], [k]: v };
    updateSection(section.id, { ...section.content, [arrKey]: newArr });
  };

  const addArrayItem = (arrKey, template) => {
    const arr = section.content[arrKey] || [];
    updateSection(section.id, { ...section.content, [arrKey]: [...arr, { ...template }] });
  };

  const removeArrayItem = (arrKey, idx) => {
    const arr = [...(section.content[arrKey] || [])];
    arr.splice(idx, 1);
    updateSection(section.id, { ...section.content, [arrKey]: arr });
  };

  const ICONS = {
    hero: '🎯', text: '📝', 'image-text': '🖼️', gallery: '🎨', features: '⭐', cta: '📢',
    marquee: '📣', testimonials: '💬', 'product-spotlight': '🛍️', stats: '📊',
    newsletter: '✉️', 'icon-grid': '🔲', 'video-hero': '🎬', 'split-cta': '⚡',
    countdown: '⏱️', 'category-grid': '🗂️', 'before-after': '🔀',
    faq: '❓', team: '👥', 'logo-strip': '🏷️', steps: '🪜',
    tabs: '📑', pricing: '💰', banner: '📯', 'product-grid': '🏪', pullquote: '💬',
    'bento-grid': '⬜', timeline: '📅', 'map-embed': '🗺️', 'contact-form': '📬', comparison: '⚖️',
    'review-summary': '🌟', accordion: '📋', 'social-feed': '📸', 'hero-slider': '🎠', 'trust-badges': '🏅',
    guarantee: '🛡️', press: '📰', ambassador: '🤝', 'size-guide': '📐', 'gift-cards': '🎁',
    'policy-page': '📋', 'about-story': '📖', 'dark-stats': '📊', 'contact-info': '📍',
  };

  const I = FieldInput;
  const T = FieldTextarea;
  const S = FieldSelect;
  const FI = FieldImagePicker;

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xl">{ICONS[section.type] ?? '📦'}</span>
          <span className="font-bold text-sm text-slate-700 capitalize">{section.type.replace(/-/g, ' ')}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => moveSection(section.id, 'up')} disabled={isFirst}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-sm font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-30 transition">↑</button>
          <button onClick={() => moveSection(section.id, 'down')} disabled={isLast}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-sm font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-30 transition">↓</button>
          <button onClick={() => deleteSection(section.id)}
            className="rounded-lg bg-red-50 border border-red-200 px-2.5 py-1 text-sm font-bold text-red-500 hover:bg-red-100 transition">✕</button>
        </div>
      </div>

      <div className="flex flex-col gap-3">

        {section.type === 'hero' && (<>
          <I label="Hero Title" value={section.content.title} onChange={e => update('title', e.target.value)} />
          <I label="Subtitle" value={section.content.subtitle} onChange={e => update('subtitle', e.target.value)} />
          <I label="Button Text" value={section.content.cta} onChange={e => update('cta', e.target.value)} />
          <I label="Button Link" value={section.content.ctaLink} onChange={e => update('ctaLink', e.target.value)} placeholder="/shop" />
          <FI label="Background Image URL" value={section.content.image} onPick={url => update('image', url)} />
        </>)}

        {section.type === 'text' && (<>
          <I label="Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <T label="Body Text" value={section.content.body} onChange={e => update('body', e.target.value)} />
        </>)}

        {section.type === 'image-text' && (<>
          <I label="Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <T label="Body Text" rows={3} value={section.content.body} onChange={e => update('body', e.target.value)} />
          <FI label="Image URL" value={section.content.image} onPick={url => update('image', url)} />
          <S label="Image Position" value={section.content.imagePosition} onChange={e => update('imagePosition', e.target.value)}
            options={[['left', 'Left'], ['right', 'Right']]} />
        </>)}

        {section.type === 'gallery' && (
          <T label="Images (URLs, one per line)" rows={4}
            value={(section.content.images || []).join('\n')}
            onChange={e => update('images', e.target.value.split('\n').filter(Boolean))} />
        )}

        {section.type === 'features' && (<>
          <S label="Columns" value={section.content.columns} onChange={e => update('columns', Number(e.target.value))}
            options={[[2,'2'],[3,'3'],[4,'4']]} />
          {(section.content.items || []).map((item, i) => (
            <div key={i} className="grid grid-cols-[56px_1fr_1fr_32px] gap-2 rounded-xl bg-white border border-slate-200 p-3 items-end">
              <I label="Icon" value={item.icon} onChange={e => updateArrayItem(section.content.items, 'items', i, 'icon', e.target.value)} />
              <I label="Title" value={item.title} onChange={e => updateArrayItem(section.content.items, 'items', i, 'title', e.target.value)} />
              <I label="Text" value={item.text} onChange={e => updateArrayItem(section.content.items, 'items', i, 'text', e.target.value)} />
              <button onClick={() => removeArrayItem('items', i)} className="text-red-400 hover:text-red-600 text-lg leading-none mb-1">✕</button>
            </div>
          ))}
          <button onClick={() => addArrayItem('items', { icon: '✨', title: 'Feature', text: 'Description' })}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Feature</button>
        </>)}

        {section.type === 'cta' && (<>
          <I label="Title" value={section.content.title} onChange={e => update('title', e.target.value)} />
          <I label="Subtitle" value={section.content.subtitle} onChange={e => update('subtitle', e.target.value)} />
          <I label="Button Text" value={section.content.buttonText} onChange={e => update('buttonText', e.target.value)} />
          <I label="Button Link" value={section.content.buttonLink} onChange={e => update('buttonLink', e.target.value)} placeholder="/shop" />
          <S label="Background Style" value={section.content.bgStyle || 'indigo'} onChange={e => update('bgStyle', e.target.value)}
            options={[['indigo','Indigo Gradient'],['dark','Dark'],['gold','Gold Gradient'],['rose','Rose']]} />
        </>)}

        {section.type === 'marquee' && (<>
          <T label="Items (one per line)" rows={4}
            value={(section.content.items || []).join('\n')}
            onChange={e => update('items', e.target.value.split('\n').filter(Boolean))} />
          <S label="Style" value={section.content.style || 'dark'} onChange={e => update('style', e.target.value)}
            options={[['dark','Dark'],['light','Light'],['gold','Gold']]} />
        </>)}

        {section.type === 'testimonials' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          {(section.content.items || []).map((item, i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
              <div className="grid grid-cols-3 gap-2">
                <I label="Name" value={item.name} onChange={e => updateArrayItem(section.content.items, 'items', i, 'name', e.target.value)} />
                <I label="Role" value={item.role} onChange={e => updateArrayItem(section.content.items, 'items', i, 'role', e.target.value)} />
                <I label="Stars (1-5)" value={item.stars} onChange={e => updateArrayItem(section.content.items, 'items', i, 'stars', e.target.value)} />
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <T label="Quote" rows={2} value={item.quote} onChange={e => updateArrayItem(section.content.items, 'items', i, 'quote', e.target.value)} />
                </div>
                <button onClick={() => removeArrayItem('items', i)} className="text-red-400 hover:text-red-600 mb-1">✕</button>
              </div>
            </div>
          ))}
          <button onClick={() => addArrayItem('items', { name: 'Jane Doe', role: 'Verified Buyer', quote: 'Absolutely love it!', stars: 5 })}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Testimonial</button>
        </>)}

        {section.type === 'product-spotlight' && (<>
          <I label="Badge Text" value={section.content.badge} onChange={e => update('badge', e.target.value)} placeholder="New Arrival" />
          <I label="Product Name" value={section.content.name} onChange={e => update('name', e.target.value)} />
          <T label="Description" rows={3} value={section.content.description} onChange={e => update('description', e.target.value)} />
          <I label="Price" value={section.content.price} onChange={e => update('price', e.target.value)} placeholder="$49.00" />
          <I label="Original Price" value={section.content.originalPrice} onChange={e => update('originalPrice', e.target.value)} placeholder="$89.00" />
          <FI label="Product Image URL" value={section.content.image} onPick={url => update('image', url)} />
          <I label="Button Text" value={section.content.buttonText} onChange={e => update('buttonText', e.target.value)} placeholder="Shop Now" />
          <I label="Button Link" value={section.content.buttonLink} onChange={e => update('buttonLink', e.target.value)} placeholder="/product/slug" />
          <S label="Layout" value={section.content.layout || 'image-left'} onChange={e => update('layout', e.target.value)}
            options={[['image-left','Image Left'],['image-right','Image Right']]} />
        </>)}

        {section.type === 'stats' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          {(section.content.stats || []).map((stat, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_56px_32px] gap-2 rounded-xl bg-white border border-slate-200 p-3 items-end">
              <I label="Value" value={stat.value} onChange={e => updateArrayItem(section.content.stats, 'stats', i, 'value', e.target.value)} />
              <I label="Label" value={stat.label} onChange={e => updateArrayItem(section.content.stats, 'stats', i, 'label', e.target.value)} />
              <I label="Icon" value={stat.icon} onChange={e => updateArrayItem(section.content.stats, 'stats', i, 'icon', e.target.value)} />
              <button onClick={() => removeArrayItem('stats', i)} className="text-red-400 hover:text-red-600 text-lg mb-1">✕</button>
            </div>
          ))}
          <button onClick={() => addArrayItem('stats', { value: '10K+', label: 'Happy Customers', icon: '😊' })}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Stat</button>
        </>)}

        {section.type === 'newsletter' && (<>
          <I label="Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <I label="Subheading" value={section.content.subheading} onChange={e => update('subheading', e.target.value)} />
          <I label="Placeholder Text" value={section.content.placeholder} onChange={e => update('placeholder', e.target.value)} placeholder="Enter your email…" />
          <I label="Button Text" value={section.content.buttonText} onChange={e => update('buttonText', e.target.value)} placeholder="Subscribe" />
          <S label="Style" value={section.content.style || 'centered'} onChange={e => update('style', e.target.value)}
            options={[['centered','Centered'],['split','Split Layout']]} />
        </>)}

        {section.type === 'icon-grid' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <S label="Columns" value={section.content.columns || 4} onChange={e => update('columns', Number(e.target.value))}
            options={[[3,'3'],[4,'4'],[5,'5'],[6,'6']]} />
          {(section.content.items || []).map((item, i) => (
            <div key={i} className="grid grid-cols-[56px_1fr_32px] gap-2 rounded-xl bg-white border border-slate-200 p-3 items-end">
              <I label="Icon" value={item.icon} onChange={e => updateArrayItem(section.content.items, 'items', i, 'icon', e.target.value)} />
              <I label="Label" value={item.label} onChange={e => updateArrayItem(section.content.items, 'items', i, 'label', e.target.value)} />
              <button onClick={() => removeArrayItem('items', i)} className="text-red-400 hover:text-red-600 text-lg mb-1">✕</button>
            </div>
          ))}
          <button onClick={() => addArrayItem('items', { icon: '🎁', label: 'Free Gifts' })}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Icon</button>
        </>)}

        {section.type === 'video-hero' && (<>
          <I label="YouTube / Video Embed URL" value={section.content.videoUrl} onChange={e => update('videoUrl', e.target.value)} placeholder="https://www.youtube.com/embed/..." />
          <I label="Overlay Title" value={section.content.title} onChange={e => update('title', e.target.value)} />
          <I label="Subtitle" value={section.content.subtitle} onChange={e => update('subtitle', e.target.value)} />
          <I label="Button Text" value={section.content.buttonText} onChange={e => update('buttonText', e.target.value)} />
          <I label="Button Link" value={section.content.buttonLink} onChange={e => update('buttonLink', e.target.value)} />
        </>)}

        {section.type === 'split-cta' && (<>
          <div className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Left Panel</p>
            <I label="Heading" value={section.content.leftHeading} onChange={e => update('leftHeading', e.target.value)} />
            <I label="Subtext" value={section.content.leftSub} onChange={e => update('leftSub', e.target.value)} />
            <I label="Button Text" value={section.content.leftBtn} onChange={e => update('leftBtn', e.target.value)} />
            <I label="Button Link" value={section.content.leftLink} onChange={e => update('leftLink', e.target.value)} />
            <FI label="Background Image URL" value={section.content.leftImage} onPick={url => update('leftImage', url)} />
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Right Panel</p>
            <I label="Heading" value={section.content.rightHeading} onChange={e => update('rightHeading', e.target.value)} />
            <I label="Subtext" value={section.content.rightSub} onChange={e => update('rightSub', e.target.value)} />
            <I label="Button Text" value={section.content.rightBtn} onChange={e => update('rightBtn', e.target.value)} />
            <I label="Button Link" value={section.content.rightLink} onChange={e => update('rightLink', e.target.value)} />
            <FI label="Background Image URL" value={section.content.rightImage} onPick={url => update('rightImage', url)} />
          </div>
        </>)}

        {section.type === 'countdown' && (<>
          <I label="Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <I label="Subheading" value={section.content.subheading} onChange={e => update('subheading', e.target.value)} />
          <I label="Target Date (ISO)" value={section.content.targetDate} onChange={e => update('targetDate', e.target.value)} placeholder="2025-12-31T23:59:59" />
          <I label="Button Text" value={section.content.buttonText} onChange={e => update('buttonText', e.target.value)} />
          <I label="Button Link" value={section.content.buttonLink} onChange={e => update('buttonLink', e.target.value)} />
          <S label="Style" value={section.content.style || 'dark'} onChange={e => update('style', e.target.value)}
            options={[['dark','Dark'],['light','Light'],['rose','Rose']]} />
        </>)}

        {section.type === 'category-grid' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          {(section.content.categories || []).map((cat, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_32px] gap-2 rounded-xl bg-white border border-slate-200 p-3 items-end">
              <I label="Name" value={cat.name} onChange={e => updateArrayItem(section.content.categories, 'categories', i, 'name', e.target.value)} />
              <FI label="Image URL" value={cat.image} onPick={url => updateArrayItem(section.content.categories, 'categories', i, 'image', url)} />
              <I label="Link" value={cat.link} onChange={e => updateArrayItem(section.content.categories, 'categories', i, 'link', e.target.value)} />
              <button onClick={() => removeArrayItem('categories', i)} className="text-red-400 hover:text-red-600 text-lg mb-1">✕</button>
            </div>
          ))}
          <button onClick={() => addArrayItem('categories', { name: 'Category', image: '', link: '/shop' })}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Category</button>
        </>)}

        {section.type === 'before-after' && (<>
          <I label="Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <FI label="Before Image URL" value={section.content.beforeImage} onPick={url => update('beforeImage', url)} />
          <I label="Before Label" value={section.content.beforeLabel} onChange={e => update('beforeLabel', e.target.value)} placeholder="Before" />
          <FI label="After Image URL" value={section.content.afterImage} onPick={url => update('afterImage', url)} />
          <I label="After Label" value={section.content.afterLabel} onChange={e => update('afterLabel', e.target.value)} placeholder="After" />
          <T label="Description" rows={2} value={section.content.description} onChange={e => update('description', e.target.value)} />
        </>)}

        {section.type === 'faq' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          {(section.content.items || []).map((item, i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <I label="Question" value={item.question} onChange={e => updateArrayItem(section.content.items, 'items', i, 'question', e.target.value)} />
                </div>
                <button onClick={() => removeArrayItem('items', i)} className="text-red-400 hover:text-red-600 mb-1">✕</button>
              </div>
              <T label="Answer" rows={2} value={item.answer} onChange={e => updateArrayItem(section.content.items, 'items', i, 'answer', e.target.value)} />
            </div>
          ))}
          <button onClick={() => addArrayItem('items', { question: 'How does it work?', answer: 'It works great!' })}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add FAQ</button>
        </>)}

        {section.type === 'team' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <I label="Subheading" value={section.content.subheading} onChange={e => update('subheading', e.target.value)} />
          {(section.content.members || []).map((m, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_32px] gap-2 rounded-xl bg-white border border-slate-200 p-3 items-end">
              <I label="Name" value={m.name} onChange={e => updateArrayItem(section.content.members, 'members', i, 'name', e.target.value)} />
              <I label="Role" value={m.role} onChange={e => updateArrayItem(section.content.members, 'members', i, 'role', e.target.value)} />
              <FI label="Photo URL" value={m.photo} onPick={url => updateArrayItem(section.content.members, 'members', i, 'photo', url)} />
              <button onClick={() => removeArrayItem('members', i)} className="text-red-400 hover:text-red-600 text-lg mb-1">✕</button>
            </div>
          ))}
          <button onClick={() => addArrayItem('members', { name: 'Jane Doe', role: 'Founder', photo: '' })}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Member</button>
        </>)}

        {section.type === 'logo-strip' && (<>
          <I label="Heading (optional)" value={section.content.heading} onChange={e => update('heading', e.target.value)} placeholder="As Featured In" />
          <T label="Logo Image URLs (one per line)" rows={4}
            value={(section.content.logos || []).join('\n')}
            onChange={e => update('logos', e.target.value.split('\n').filter(Boolean))} />
          <S label="Style" value={section.content.style || 'grayscale'} onChange={e => update('style', e.target.value)}
            options={[['grayscale','Greyscale'],['color','Color'],['outlined','Outlined Box']]} />
        </>)}

        {section.type === 'steps' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <I label="Subheading" value={section.content.subheading} onChange={e => update('subheading', e.target.value)} />
          <S label="Layout" value={section.content.layout || 'horizontal'} onChange={e => update('layout', e.target.value)}
            options={[['horizontal','Horizontal'],['vertical','Vertical']]} />
          {(section.content.steps || []).map((step, i) => (
            <div key={i} className="grid grid-cols-[56px_1fr_1fr_32px] gap-2 rounded-xl bg-white border border-slate-200 p-3 items-end">
              <I label="Icon" value={step.icon} onChange={e => updateArrayItem(section.content.steps, 'steps', i, 'icon', e.target.value)} />
              <I label="Title" value={step.title} onChange={e => updateArrayItem(section.content.steps, 'steps', i, 'title', e.target.value)} />
              <I label="Description" value={step.description} onChange={e => updateArrayItem(section.content.steps, 'steps', i, 'description', e.target.value)} />
              <button onClick={() => removeArrayItem('steps', i)} className="text-red-400 hover:text-red-600 text-lg mb-1">✕</button>
            </div>
          ))}
          <button onClick={() => addArrayItem('steps', { icon: '📦', title: 'Step', description: 'Description here' })}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Step</button>
        </>)}

        {/* ════════════════════════════════
            20 NEW SECTION EDITORS
        ════════════════════════════════ */}

        {/* 1. TABBED CONTENT */}
        {section.type === 'tabs' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          {(section.content.tabs||[]).map((tab,i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
              <div className="flex gap-2 items-end">
                <div className="flex-1"><I label="Tab Label" value={tab.label} onChange={e => updateArrayItem(section.content.tabs,'tabs',i,'label',e.target.value)} /></div>
                <button onClick={() => removeArrayItem('tabs',i)} className="text-red-400 hover:text-red-600 mb-1">✕</button>
              </div>
              <T label="Content" rows={3} value={tab.content} onChange={e => updateArrayItem(section.content.tabs,'tabs',i,'content',e.target.value)} />
              <FI label="Image URL (optional)" value={tab.image} onPick={url => updateArrayItem(section.content.tabs,'tabs',i,'image',url)} />
            </div>
          ))}
          <button onClick={() => addArrayItem('tabs',{label:'Tab',content:'Content here',image:''})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Tab</button>
        </>)}

        {/* 2. PRICING TABLE */}
        {section.type === 'pricing' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <I label="Subheading" value={section.content.subheading} onChange={e => update('subheading', e.target.value)} />
          {(section.content.plans||[]).map((plan,i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
              <div className="grid grid-cols-3 gap-2">
                <I label="Plan Name" value={plan.name} onChange={e => updateArrayItem(section.content.plans,'plans',i,'name',e.target.value)} />
                <I label="Price" value={plan.price} onChange={e => updateArrayItem(section.content.plans,'plans',i,'price',e.target.value)} placeholder="$29/mo" />
                <I label="Button Text" value={plan.cta} onChange={e => updateArrayItem(section.content.plans,'plans',i,'cta',e.target.value)} placeholder="Get Started" />
              </div>
              <T label="Features (one per line)" rows={4} value={(plan.features||[]).join('\n')} onChange={e => updateArrayItem(section.content.plans,'plans',i,'features',e.target.value.split('\n').filter(Boolean))} />
              <div className="flex gap-3 items-center">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={!!plan.highlighted} onChange={e => updateArrayItem(section.content.plans,'plans',i,'highlighted',e.target.checked)} className="accent-indigo-600" />
                  <span className="text-xs text-slate-600">Highlight this plan</span>
                </label>
                <button onClick={() => removeArrayItem('plans',i)} className="ml-auto text-red-400 hover:text-red-600 text-xs">Remove</button>
              </div>
            </div>
          ))}
          <button onClick={() => addArrayItem('plans',{name:'Pro',price:'$29/mo',cta:'Get Started',features:['Feature 1','Feature 2','Feature 3'],highlighted:false})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Plan</button>
        </>)}

        {/* 3. ANNOUNCEMENT BANNER */}
        {section.type === 'banner' && (<>
          <I label="Message" value={section.content.message} onChange={e => update('message', e.target.value)} placeholder="🎉 Summer sale — 20% off everything!" />
          <I label="Link Text" value={section.content.linkText} onChange={e => update('linkText', e.target.value)} placeholder="Shop now →" />
          <I label="Link URL" value={section.content.linkUrl} onChange={e => update('linkUrl', e.target.value)} placeholder="/shop" />
          <S label="Style" value={section.content.style||'indigo'} onChange={e => update('style',e.target.value)}
            options={[['indigo','Indigo'],['rose','Rose'],['amber','Amber'],['emerald','Emerald'],['dark','Dark']]} />
        </>)}

        {/* 4. PRODUCT GRID */}
        {section.type === 'product-grid' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <S label="Columns" value={section.content.columns||3} onChange={e => update('columns',Number(e.target.value))}
            options={[[2,'2'],[3,'3'],[4,'4']]} />
          {(section.content.products||[]).map((p,i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
              <div className="grid grid-cols-2 gap-2">
                <I label="Name" value={p.name} onChange={e => updateArrayItem(section.content.products,'products',i,'name',e.target.value)} />
                <I label="Price" value={p.price} onChange={e => updateArrayItem(section.content.products,'products',i,'price',e.target.value)} />
                <FI label="Image URL" value={p.image} onPick={url => updateArrayItem(section.content.products,'products',i,'image',url)} />
                <I label="Link" value={p.link} onChange={e => updateArrayItem(section.content.products,'products',i,'link',e.target.value)} />
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1"><I label="Badge (optional)" value={p.badge} onChange={e => updateArrayItem(section.content.products,'products',i,'badge',e.target.value)} placeholder="New" /></div>
                <button onClick={() => removeArrayItem('products',i)} className="text-red-400 hover:text-red-600 text-xs mb-2">Remove</button>
              </div>
            </div>
          ))}
          <button onClick={() => addArrayItem('products',{name:'Product Name',price:'$49.00',image:'',link:'/product',badge:''})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Product</button>
        </>)}

        {/* 5. PULL QUOTE */}
        {section.type === 'pullquote' && (<>
          <T label="Quote Text" rows={3} value={section.content.quote} onChange={e => update('quote', e.target.value)} />
          <I label="Attribution Name" value={section.content.name} onChange={e => update('name', e.target.value)} placeholder="— Steve Jobs" />
          <I label="Attribution Role" value={section.content.role} onChange={e => update('role', e.target.value)} placeholder="Co-founder, Apple" />
          <S label="Style" value={section.content.style||'centered'} onChange={e => update('style',e.target.value)}
            options={[['centered','Centered'],['left-border','Left Border'],['dark','Dark Bg']]} />
        </>)}

        {/* 6. BENTO GRID */}
        {section.type === 'bento-grid' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          {(section.content.cells||[]).map((cell,i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
              <div className="grid grid-cols-[1fr_1fr_80px_32px] gap-2 items-end">
                <I label="Title" value={cell.title} onChange={e => updateArrayItem(section.content.cells,'cells',i,'title',e.target.value)} />
                <FI label="Image URL" value={cell.image} onPick={url => updateArrayItem(section.content.cells,'cells',i,'image',url)} />
                <S label="Width" value={cell.size||'1'} onChange={e => updateArrayItem(section.content.cells,'cells',i,'size',e.target.value)}
                  options={[['1','Normal'],['2','Wide']]} />
                <button onClick={() => removeArrayItem('cells',i)} className="text-red-400 hover:text-red-600 text-lg mb-1">✕</button>
              </div>
              <I label="Description" value={cell.desc} onChange={e => updateArrayItem(section.content.cells,'cells',i,'desc',e.target.value)} />
            </div>
          ))}
          <button onClick={() => addArrayItem('cells',{title:'Feature',desc:'Description',image:'',size:'1'})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Cell</button>
        </>)}

        {/* 7. TIMELINE */}
        {section.type === 'timeline' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          {(section.content.events||[]).map((ev,i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
              <div className="grid grid-cols-[120px_1fr_32px] gap-2 items-end">
                <I label="Year / Date" value={ev.year} onChange={e => updateArrayItem(section.content.events,'events',i,'year',e.target.value)} />
                <I label="Title" value={ev.title} onChange={e => updateArrayItem(section.content.events,'events',i,'title',e.target.value)} />
                <button onClick={() => removeArrayItem('events',i)} className="text-red-400 hover:text-red-600 text-lg mb-1">✕</button>
              </div>
              <T label="Description" rows={2} value={ev.desc} onChange={e => updateArrayItem(section.content.events,'events',i,'desc',e.target.value)} />
            </div>
          ))}
          <button onClick={() => addArrayItem('events',{year:'2024',title:'Milestone',desc:'Description of this event'})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Event</button>
        </>)}

        {/* 8. MAP EMBED */}
        {section.type === 'map-embed' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} placeholder="Find Us" />
          <I label="Google Maps Embed URL" value={section.content.embedUrl} onChange={e => update('embedUrl', e.target.value)} placeholder="https://maps.google.com/maps?..." />
          <I label="Address Line 1" value={section.content.address1} onChange={e => update('address1', e.target.value)} />
          <I label="Address Line 2" value={section.content.address2} onChange={e => update('address2', e.target.value)} />
          <I label="Phone" value={section.content.phone} onChange={e => update('phone', e.target.value)} />
          <I label="Email" value={section.content.email} onChange={e => update('email', e.target.value)} />
          <I label="Hours" value={section.content.hours} onChange={e => update('hours', e.target.value)} placeholder="Mon–Fri 9am–6pm" />
        </>)}

        {/* 9. CONTACT FORM */}
        {section.type === 'contact-form' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <I label="Subheading" value={section.content.subheading} onChange={e => update('subheading', e.target.value)} />
          <I label="Submit Button Text" value={section.content.buttonText} onChange={e => update('buttonText', e.target.value)} placeholder="Send Message" />
          <S label="Style" value={section.content.style||'split'} onChange={e => update('style',e.target.value)}
            options={[['split','Split with Info'],['centered','Centered Card']]} />
          <I label="Contact Email (display)" value={section.content.contactEmail} onChange={e => update('contactEmail', e.target.value)} />
          <I label="Contact Phone (display)" value={section.content.contactPhone} onChange={e => update('contactPhone', e.target.value)} />
        </>)}

        {/* 10. COMPARISON TABLE */}
        {section.type === 'comparison' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <I label="Left Column Header" value={section.content.leftHeader} onChange={e => update('leftHeader', e.target.value)} placeholder="Our Product" />
          <I label="Right Column Header" value={section.content.rightHeader} onChange={e => update('rightHeader', e.target.value)} placeholder="Competitors" />
          {(section.content.rows||[]).map((row,i) => (
            <div key={i} className="grid grid-cols-[1fr_80px_80px_32px] gap-2 rounded-xl bg-white border border-slate-200 p-3 items-end">
              <I label="Feature" value={row.feature} onChange={e => updateArrayItem(section.content.rows,'rows',i,'feature',e.target.value)} />
              <I label="Ours" value={row.ours} onChange={e => updateArrayItem(section.content.rows,'rows',i,'ours',e.target.value)} placeholder="✓" />
              <I label="Theirs" value={row.theirs} onChange={e => updateArrayItem(section.content.rows,'rows',i,'theirs',e.target.value)} placeholder="✗" />
              <button onClick={() => removeArrayItem('rows',i)} className="text-red-400 hover:text-red-600 text-lg mb-1">✕</button>
            </div>
          ))}
          <button onClick={() => addArrayItem('rows',{feature:'Feature name',ours:'✓',theirs:'✗'})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Row</button>
        </>)}

        {/* 11. REVIEW SUMMARY */}
        {section.type === 'review-summary' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <I label="Overall Rating (e.g. 4.8)" value={section.content.rating} onChange={e => update('rating', e.target.value)} />
          <I label="Total Reviews" value={section.content.total} onChange={e => update('total', e.target.value)} placeholder="2,847 reviews" />
          {[5,4,3,2,1].map(star => (
            <div key={star} className="flex items-center gap-2">
              <span className="text-xs font-bold text-amber-500 w-8">{star}★</span>
              <div className="flex-1"><I label="" value={(section.content.breakdown||{})[star]||''} onChange={e => update('breakdown',{...(section.content.breakdown||{}),[star]:e.target.value})} placeholder="75%" /></div>
            </div>
          ))}
        </>)}

        {/* 12. ACCORDION */}
        {section.type === 'accordion' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <S label="Style" value={section.content.style||'default'} onChange={e => update('style',e.target.value)}
            options={[['default','Default'],['filled','Filled Cards'],['minimal','Minimal Lines']]} />
          {(section.content.items||[]).map((item,i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
              <div className="flex gap-2 items-end">
                <div className="flex-1"><I label="Title" value={item.title} onChange={e => updateArrayItem(section.content.items,'items',i,'title',e.target.value)} /></div>
                <button onClick={() => removeArrayItem('items',i)} className="text-red-400 hover:text-red-600 mb-1">✕</button>
              </div>
              <T label="Content" rows={2} value={item.content} onChange={e => updateArrayItem(section.content.items,'items',i,'content',e.target.value)} />
            </div>
          ))}
          <button onClick={() => addArrayItem('items',{title:'Section Title',content:'Content here...'})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Item</button>
        </>)}

        {/* 13. SOCIAL FEED */}
        {section.type === 'social-feed' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} placeholder="Follow us on Instagram" />
          <I label="Handle / Username" value={section.content.handle} onChange={e => update('handle', e.target.value)} placeholder="@yourbrand" />
          <I label="Profile Link" value={section.content.profileLink} onChange={e => update('profileLink', e.target.value)} placeholder="https://instagram.com/yourbrand" />
          <T label="Post Image URLs (one per line, up to 8)" rows={5}
            value={(section.content.posts||[]).join('\n')}
            onChange={e => update('posts', e.target.value.split('\n').filter(Boolean))} />
        </>)}

        {/* 14. HERO SLIDER */}
        {section.type === 'hero-slider' && (<>
          {(section.content.slides||[]).map((slide,i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Slide {i+1}</span>
                <button onClick={() => removeArrayItem('slides',i)} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
              </div>
              <I label="Title" value={slide.title} onChange={e => updateArrayItem(section.content.slides,'slides',i,'title',e.target.value)} />
              <I label="Subtitle" value={slide.subtitle} onChange={e => updateArrayItem(section.content.slides,'slides',i,'subtitle',e.target.value)} />
              <I label="Button Text" value={slide.cta} onChange={e => updateArrayItem(section.content.slides,'slides',i,'cta',e.target.value)} />
              <I label="Button Link" value={slide.link} onChange={e => updateArrayItem(section.content.slides,'slides',i,'link',e.target.value)} />
              <FI label="Background Image URL" value={slide.image} onPick={url => updateArrayItem(section.content.slides,'slides',i,'image',url)} />
            </div>
          ))}
          <button onClick={() => addArrayItem('slides',{title:'New Arrival',subtitle:'Discover the collection',cta:'Shop Now',link:'/shop',image:'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1400&q=80'})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Slide</button>
        </>)}

        {/* 15. TRUST BADGES */}
        {section.type === 'trust-badges' && (<>
          <I label="Section Heading (optional)" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <S label="Layout" value={section.content.layout||'row'} onChange={e => update('layout',e.target.value)}
            options={[['row','Horizontal Row'],['grid','Grid'],['cards','Bordered Cards']]} />
          {(section.content.badges||[]).map((b,i) => (
            <div key={i} className="grid grid-cols-[56px_1fr_1fr_32px] gap-2 rounded-xl bg-white border border-slate-200 p-3 items-end">
              <I label="Icon" value={b.icon} onChange={e => updateArrayItem(section.content.badges,'badges',i,'icon',e.target.value)} />
              <I label="Title" value={b.title} onChange={e => updateArrayItem(section.content.badges,'badges',i,'title',e.target.value)} />
              <I label="Subtitle" value={b.subtitle} onChange={e => updateArrayItem(section.content.badges,'badges',i,'subtitle',e.target.value)} />
              <button onClick={() => removeArrayItem('badges',i)} className="text-red-400 hover:text-red-600 text-lg mb-1">✕</button>
            </div>
          ))}
          <button onClick={() => addArrayItem('badges',{icon:'🔒',title:'Secure',subtitle:'SSL Encrypted'})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Badge</button>
        </>)}

        {/* 16. GUARANTEE */}
        {section.type === 'guarantee' && (<>
          <I label="Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <T label="Body Text" rows={3} value={section.content.body} onChange={e => update('body', e.target.value)} />
          <I label="Badge Icon / Emoji" value={section.content.icon} onChange={e => update('icon', e.target.value)} placeholder="🛡️" />
          <I label="Guarantee Label" value={section.content.label} onChange={e => update('label', e.target.value)} placeholder="30-Day Money Back Guarantee" />
          <I label="Button Text" value={section.content.buttonText} onChange={e => update('buttonText', e.target.value)} />
          <I label="Button Link" value={section.content.buttonLink} onChange={e => update('buttonLink', e.target.value)} />
          <S label="Style" value={section.content.style||'light'} onChange={e => update('style',e.target.value)}
            options={[['light','Light'],['dark','Dark'],['gold','Gold']]} />
        </>)}

        {/* 17. PRESS QUOTES */}
        {section.type === 'press' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} placeholder="What the Press Says" />
          {(section.content.items||[]).map((item,i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
              <div className="grid grid-cols-[1fr_1fr_32px] gap-2 items-end">
                <I label="Publication" value={item.publication} onChange={e => updateArrayItem(section.content.items,'items',i,'publication',e.target.value)} />
                <FI label="Logo URL (optional)" value={item.logo} onPick={url => updateArrayItem(section.content.items,'items',i,'logo',url)} />
                <button onClick={() => removeArrayItem('items',i)} className="text-red-400 hover:text-red-600 mb-1">✕</button>
              </div>
              <T label="Quote" rows={2} value={item.quote} onChange={e => updateArrayItem(section.content.items,'items',i,'quote',e.target.value)} />
            </div>
          ))}
          <button onClick={() => addArrayItem('items',{publication:'Forbes',logo:'',quote:'"A game-changer in the industry."'})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Quote</button>
        </>)}

        {/* 18. AMBASSADOR */}
        {section.type === 'ambassador' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <I label="Subheading" value={section.content.subheading} onChange={e => update('subheading', e.target.value)} />
          {(section.content.people||[]).map((p,i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_80px_1fr_32px] gap-2 rounded-xl bg-white border border-slate-200 p-3 items-end">
              <I label="Name" value={p.name} onChange={e => updateArrayItem(section.content.people,'people',i,'name',e.target.value)} />
              <I label="Handle" value={p.handle} onChange={e => updateArrayItem(section.content.people,'people',i,'handle',e.target.value)} placeholder="@name" />
              <I label="Followers" value={p.followers} onChange={e => updateArrayItem(section.content.people,'people',i,'followers',e.target.value)} placeholder="120K" />
              <FI label="Photo URL" value={p.photo} onPick={url => updateArrayItem(section.content.people,'people',i,'photo',url)} />
              <button onClick={() => removeArrayItem('people',i)} className="text-red-400 hover:text-red-600 text-lg mb-1">✕</button>
            </div>
          ))}
          <button onClick={() => addArrayItem('people',{name:'Jane Doe',handle:'@janedoe',followers:'50K',photo:''})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Ambassador</button>
        </>)}

        {/* 19. SIZE GUIDE */}
        {section.type === 'size-guide' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <I label="Subheading" value={section.content.subheading} onChange={e => update('subheading', e.target.value)} />
          <I label="Column Headers (comma-separated)" value={section.content.headers||''} onChange={e => update('headers', e.target.value)} placeholder="Size, Chest, Waist, Hip" />
          {(section.content.rows||[]).map((row,i) => (
            <div key={i} className="flex gap-2 items-end">
              <div className="flex-1"><I label={`Row ${i+1} (comma-separated)`} value={row} onChange={e => { const a=[...(section.content.rows||[])]; a[i]=e.target.value; update('rows',a); }} /></div>
              <button onClick={() => removeArrayItem('rows',i)} className="text-red-400 hover:text-red-600 mb-1">✕</button>
            </div>
          ))}
          <button onClick={() => { const a=[...(section.content.rows||[]),'S, 34–36, 28–30, 36–38']; update('rows',a); }}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Row</button>
        </>)}

        {/* 20. GIFT CARDS */}
        {section.type === 'gift-cards' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <I label="Subheading" value={section.content.subheading} onChange={e => update('subheading', e.target.value)} />
          <FI label="Card Image URL" value={section.content.cardImage} onPick={url => update('cardImage', url)} />
          <T label="Description" rows={3} value={section.content.description} onChange={e => update('description', e.target.value)} />
          <I label="Button Text" value={section.content.buttonText} onChange={e => update('buttonText', e.target.value)} placeholder="Send a Gift Card" />
          <I label="Button Link" value={section.content.buttonLink} onChange={e => update('buttonLink', e.target.value)} placeholder="/gift-cards" />
          <I label="Denominations (comma-separated)" value={section.content.amounts||''} onChange={e => update('amounts', e.target.value)} placeholder="$25, $50, $100, $200" />
        </>)}

        {/* POLICY PAGE */}
        {section.type === 'policy-page' && (<>
          <I label="Title" value={section.content.title} onChange={e => update('title', e.target.value)} placeholder="Privacy Policy" />
          <I label="Icon (emoji)" value={section.content.icon} onChange={e => update('icon', e.target.value)} placeholder="🔒" />
          <I label="Last Updated" value={section.content.lastUpdated} onChange={e => update('lastUpdated', e.target.value)} placeholder="January 1, 2025" />
          <T label="Alert Banner Text" rows={2} value={section.content.alertText} onChange={e => update('alertText', e.target.value)} />
          <S label="Alert Color" value={section.content.alertColor||'orange'} onChange={e => update('alertColor', e.target.value)}
            options={[['orange','Orange'],['blue','Blue'],['green','Green'],['red','Red']]} />
          {(section.content.items||[]).map((item,i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-2">
              <div className="flex gap-2 items-end">
                <div className="flex-1"><I label="Section Title" value={item.title} onChange={e => updateArrayItem(section.content.items,'items',i,'title',e.target.value)} /></div>
                <button onClick={() => removeArrayItem('items',i)} className="text-red-400 hover:text-red-600 mb-1">✕</button>
              </div>
              <T label="Body" rows={3} value={item.body} onChange={e => updateArrayItem(section.content.items,'items',i,'body',e.target.value)} />
            </div>
          ))}
          <button onClick={() => addArrayItem('items',{title:'Section Title',body:'Section body text here.'})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Section</button>
        </>)}

        {/* ABOUT STORY */}
        {section.type === 'about-story' && (<>
          <I label="Eyebrow Text" value={section.content.eyebrow} onChange={e => update('eyebrow', e.target.value)} placeholder="The Beginning" />
          <I label="Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          <T label="Body (separate paragraphs with blank line)" rows={6} value={section.content.body} onChange={e => update('body', e.target.value)} />
          <I label="CTA Button Text" value={section.content.ctaText} onChange={e => update('ctaText', e.target.value)} placeholder="Order Now" />
          <I label="CTA Button Link" value={section.content.ctaLink} onChange={e => update('ctaLink', e.target.value)} placeholder="/menu" />
          <FI label="Image URL" value={section.content.image} onPick={url => update('image', url)} />
          <S label="Image Position" value={section.content.imagePosition||'right'} onChange={e => update('imagePosition', e.target.value)}
            options={[['right','Right'],['left','Left']]} />
          <I label="Year Badge" value={section.content.yearBadge} onChange={e => update('yearBadge', e.target.value)} placeholder="2020" />
          <I label="Year Badge Label" value={section.content.yearLabel} onChange={e => update('yearLabel', e.target.value)} placeholder="Founded in Hyderabad" />
        </>)}

        {/* DARK STATS */}
        {section.type === 'dark-stats' && (<>
          <I label="Heading (optional)" value={section.content.heading} onChange={e => update('heading', e.target.value)} />
          {(section.content.items||[]).map((item,i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_32px] gap-2 rounded-xl bg-white border border-slate-200 p-3 items-end">
              <I label="Value" value={item.value} onChange={e => updateArrayItem(section.content.items,'items',i,'value',e.target.value)} />
              <I label="Label" value={item.label} onChange={e => updateArrayItem(section.content.items,'items',i,'label',e.target.value)} />
              <I label="Sub-label" value={item.sub} onChange={e => updateArrayItem(section.content.items,'items',i,'sub',e.target.value)} />
              <button onClick={() => removeArrayItem('items',i)} className="text-red-400 hover:text-red-600 text-lg mb-1">✕</button>
            </div>
          ))}
          <button onClick={() => addArrayItem('items',{value:'99%',label:'Satisfaction',sub:'5,000+ reviews'})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Stat</button>
        </>)}

        {/* CONTACT INFO */}
        {section.type === 'contact-info' && (<>
          <I label="Section Heading" value={section.content.heading} onChange={e => update('heading', e.target.value)} placeholder="Contact Info" />
          {(section.content.items||[]).map((item,i) => (
            <div key={i} className="grid grid-cols-[56px_1fr_1fr_32px] gap-2 rounded-xl bg-white border border-slate-200 p-3 items-end">
              <I label="Icon" value={item.icon} onChange={e => updateArrayItem(section.content.items,'items',i,'icon',e.target.value)} />
              <I label="Label" value={item.label} onChange={e => updateArrayItem(section.content.items,'items',i,'label',e.target.value)} />
              <I label="Value" value={item.value} onChange={e => updateArrayItem(section.content.items,'items',i,'value',e.target.value)} />
              <button onClick={() => removeArrayItem('items',i)} className="text-red-400 hover:text-red-600 text-lg mb-1">✕</button>
            </div>
          ))}
          <button onClick={() => addArrayItem('items',{icon:'📍',label:'Address',value:'123 Main St'})}
            className="rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-500 hover:bg-white transition">+ Add Info Row</button>
          <I label="Corporate Box Heading" value={section.content.corporateHeading} onChange={e => update('corporateHeading', e.target.value)} placeholder="Corporate Catering" />
          <T label="Corporate Box Body" rows={2} value={section.content.corporateBody} onChange={e => update('corporateBody', e.target.value)} />
          <I label="Corporate Button Text" value={section.content.corporateBtnText} onChange={e => update('corporateBtnText', e.target.value)} placeholder="Enquire Now" />
          <I label="Corporate Button Link" value={section.content.corporateBtnLink} onChange={e => update('corporateBtnLink', e.target.value)} placeholder="/contact" />
        </>)}

      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   NEW STATEFUL HELPER COMPONENTS
═══════════════════════════════════════════ */
function TabbedSection({ s }) {
  const [active, setActive] = useState(0);
  const tabs = s.tabs || [];
  const cur = tabs[active] || {};
  return (
    <div className="max-w-5xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-10">{s.heading}</h2>}
      <div className="flex gap-2 flex-wrap justify-center mb-10">
        {tabs.map((tab, i) => (
          <button key={i} onClick={() => setActive(i)}
            className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all ${i===active ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            {tab.label}
          </button>
        ))}
      </div>
      <div className="rounded-3xl overflow-hidden border border-slate-100 bg-white shadow-sm">
        {cur.image && <img src={cur.image} alt={cur.label} className="w-full h-64 object-cover" />}
        <div className="p-10">
          <p className="text-slate-600 leading-relaxed text-lg whitespace-pre-wrap">{cur.content}</p>
        </div>
      </div>
    </div>
  );
}

function AccordionSection({ s }) {
  const [open, setOpen] = useState(null);
  const items = s.items || [];
  const toggle = i => setOpen(o => o === i ? null : i);

  if (s.style === 'minimal') return (
    <div className="max-w-3xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">{s.heading}</h2>}
      <div className="flex flex-col divide-y divide-slate-200">
        {items.map((item, i) => (
          <div key={i}>
            <button onClick={() => toggle(i)} className="w-full flex justify-between items-center py-5 text-left font-semibold text-slate-800 hover:text-indigo-600 transition-colors">
              {item.title}
              <span className={`text-xl font-light text-indigo-400 transition-transform duration-200 flex-shrink-0 ml-4 ${open===i?'rotate-45':''}`}>+</span>
            </button>
            {open === i && <p className="pb-5 text-slate-500 leading-relaxed">{item.content}</p>}
          </div>
        ))}
      </div>
    </div>
  );

  if (s.style === 'filled') return (
    <div className="max-w-3xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">{s.heading}</h2>}
      <div className="flex flex-col gap-3">
        {items.map((item, i) => (
          <div key={i} className={`rounded-2xl overflow-hidden transition-all ${open===i?'bg-indigo-600 shadow-lg':'bg-slate-100 hover:bg-slate-200'}`}>
            <button onClick={() => toggle(i)} className={`w-full flex justify-between items-center px-6 py-4 text-left font-semibold transition-colors ${open===i?'text-white':'text-slate-800'}`}>
              {item.title}
              <span className={`text-xl font-light transition-transform duration-200 flex-shrink-0 ml-4 ${open===i?'rotate-45 text-white':'text-indigo-400'}`}>+</span>
            </button>
            {open === i && <p className="px-6 pb-5 text-indigo-100 leading-relaxed">{item.content}</p>}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">{s.heading}</h2>}
      <div className="flex flex-col gap-3">
        {items.map((item, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <button onClick={() => toggle(i)} className="w-full flex justify-between items-center px-6 py-5 text-left font-semibold text-slate-800 hover:bg-slate-50 transition">
              {item.title}
              <span className={`text-indigo-500 text-xl font-light transition-transform duration-200 flex-shrink-0 ml-4 ${open===i?'rotate-45':''}`}>+</span>
            </button>
            {open === i && <div className="px-6 pb-5 text-slate-500 leading-relaxed text-sm border-t border-slate-100 pt-4">{item.content}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function HeroSlider({ s }) {
  const [cur, setCur] = useState(0);
  const slides = s.slides || [];
  if (!slides.length) return null;
  const slide = slides[cur];
  const prev = () => setCur(c => (c - 1 + slides.length) % slides.length);
  const next = () => setCur(c => (c + 1) % slides.length);
  return (
    <div className="relative overflow-hidden" style={{ minHeight: 560 }}>
      <div className="absolute inset-0 transition-all duration-700"
        style={{ background: `linear-gradient(rgba(10,10,20,.55),rgba(10,10,20,.55)) center/cover, url(${slide.image}) center/cover` }} />
      <div className="relative z-10 flex flex-col items-center justify-center text-center text-white px-6 py-28 min-h-[560px]">
        <h1 className="text-6xl font-bold mb-5 leading-tight tracking-tight max-w-3xl">{slide.title}</h1>
        {slide.subtitle && <p className="text-xl mb-10 opacity-85 max-w-xl leading-relaxed">{slide.subtitle}</p>}
        {slide.cta && <a href={slide.link||'#'} className="inline-block rounded-full bg-white text-slate-900 font-bold px-9 py-4 text-base shadow-2xl hover:scale-105 transition-transform">{slide.cta}</a>}
      </div>
      {slides.length > 1 && (<>
        <button onClick={prev} className="absolute left-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/20 hover:bg-white/40 text-white backdrop-blur-sm flex items-center justify-center text-xl transition-colors">‹</button>
        <button onClick={next} className="absolute right-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/20 hover:bg-white/40 text-white backdrop-blur-sm flex items-center justify-center text-xl transition-colors">›</button>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {slides.map((_,i) => <button key={i} onClick={()=>setCur(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${i===cur?'bg-white scale-125':'bg-white/40 hover:bg-white/60'}`} />)}
        </div>
      </>)}
    </div>
  );
}


/* ═══════════════════════════════════════════
   FAQ ACCORDION ITEM
═══════════════════════════════════════════ */
function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-5 text-left font-semibold text-slate-800 hover:bg-slate-50 transition">
        <span>{question}</span>
        <span className={`text-indigo-500 text-xl font-light transition-transform duration-200 ${open ? 'rotate-45' : ''}`}>+</span>
      </button>
      {open && (
        <div className="px-6 pb-5 text-slate-500 leading-relaxed text-sm border-t border-slate-100 pt-4">
          {answer}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   COUNTDOWN HOOK + BLOCK
═══════════════════════════════════════════ */
function useCountdown(targetDate) {
  const [time, setTime] = useState({ d: 0, h: 0, m: 0, s: 0 });
  React.useEffect(() => {
    const tick = () => {
      const diff = new Date(targetDate) - new Date();
      if (diff <= 0) return setTime({ d: 0, h: 0, m: 0, s: 0 });
      setTime({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return time;
}

function CountdownBlock({ s }) {
  const time = useCountdown(s.targetDate);
  const themes = {
    dark:  { wrap: 'bg-slate-900 text-white',   num: 'bg-slate-800 text-white',   sub: 'text-slate-400' },
    light: { wrap: 'bg-slate-50 text-slate-900', num: 'bg-white text-slate-900 shadow-md', sub: 'text-slate-500' },
    rose:  { wrap: 'bg-gradient-to-br from-rose-600 to-pink-600 text-white', num: 'bg-white/20 text-white', sub: 'text-rose-200' },
  };
  const t = themes[s.style] || themes.dark;
  return (
    <div className={`${t.wrap} py-20 px-6 text-center`}>
      <h2 className="text-4xl font-bold mb-3">{s.heading}</h2>
      {s.subheading && <p className="opacity-75 mb-12 text-lg">{s.subheading}</p>}
      <div className="flex items-center justify-center gap-4 md:gap-8 mb-12 flex-wrap">
        {[['d','Days'],['h','Hours'],['m','Mins'],['s','Secs']].map(([k, label]) => (
          <div key={k} className="text-center">
            <div className={`${t.num} rounded-2xl w-24 h-24 flex items-center justify-center text-5xl font-black tabular-nums mx-auto`}>
              {String(time[k]).padStart(2,'0')}
            </div>
            <div className={`text-xs font-bold uppercase tracking-widest mt-2 ${t.sub}`}>{label}</div>
          </div>
        ))}
      </div>
      {s.buttonText && (
        <a href={s.buttonLink || '#'}
          className="inline-block rounded-full bg-white text-slate-900 font-bold px-9 py-4 text-base hover:scale-105 transition-transform shadow-xl">
          {s.buttonText}
        </a>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   CONTACT FORM BLOCK (live, submits to API)
═══════════════════════════════════════════ */
function ContactFormBlock({ section }) {
  const s = section.content;
  const pageSlug = window.location.pathname.replace(/^\//, '');

  const empty = { name: '', email: '', subject: '', message: '' };
  const [form, setForm]       = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]   = useState(null); // 'success' | 'error'

  const fld = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const res = await axios.post(`${apiUrl}/contacts`, { ...form, page_slug: pageSlug });
      if (res.data.status) {
        setResult('success');
        setForm(empty);
      } else {
        setResult('error');
      }
    } catch (_) {
      setResult('error');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-400 transition";

  const formFields = (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {result === 'success' && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium px-4 py-3">
          ✅ Message sent! We'll get back to you soon.
        </div>
      )}
      {result === 'error' && (
        <div className="rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm font-medium px-4 py-3">
          ❌ Something went wrong. Please try again.
        </div>
      )}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Name</label>
        <input required type="text" value={form.name} onChange={e => fld('name', e.target.value)} className={inputCls} placeholder="Your name" />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Email</label>
        <input required type="email" value={form.email} onChange={e => fld('email', e.target.value)} className={inputCls} placeholder="your@email.com" />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Subject</label>
        <input type="text" value={form.subject} onChange={e => fld('subject', e.target.value)} className={inputCls} placeholder="Subject" />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Message</label>
        <textarea required rows={4} value={form.message} onChange={e => fld('message', e.target.value)} className={`${inputCls} resize-none`} placeholder="Your message..." />
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="rounded-2xl bg-indigo-600 text-white font-bold py-3.5 text-sm hover:bg-indigo-700 transition disabled:opacity-60"
      >
        {submitting ? 'Sending…' : (s.buttonText || 'Send Message')}
      </button>
    </form>
  );

  if (s.style === 'split') return (
    <div className="max-w-5xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-3">{s.heading}</h2>}
      {s.subheading && <p className="text-slate-500 text-center mb-12">{s.subheading}</p>}
      <div className="grid md:grid-cols-[1fr_1.5fr] gap-0 bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-indigo-600 p-10 text-white flex flex-col gap-6">
          <h3 className="text-2xl font-bold">Get in Touch</h3>
          {s.contactEmail && <div className="flex gap-3 items-center"><span>✉️</span><span>{s.contactEmail}</span></div>}
          {s.contactPhone && <div className="flex gap-3 items-center"><span>📞</span><span>{s.contactPhone}</span></div>}
        </div>
        <div className="p-10">{formFields}</div>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-3">{s.heading}</h2>}
      {s.subheading && <p className="text-slate-500 text-center mb-10">{s.subheading}</p>}
      <div className="bg-white rounded-3xl shadow-xl p-10 border border-slate-100">{formFields}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   SECTION BLOCK RENDERER
═══════════════════════════════════════════ */
function SectionBlock({ section }) {
  const s = section.content;

  if (section.type === 'hero') return (
    <div className="relative min-h-[600px] flex items-center justify-center overflow-hidden"
      style={{ background: `linear-gradient(rgba(10,10,20,.55),rgba(10,10,20,.55)) center/cover, url(${s.image}) center/cover` }}>
      <div className="relative z-10 text-center text-white px-6 py-20 max-w-3xl mx-auto">
        <h1 className="text-6xl font-bold mb-5 leading-tight tracking-tight">{s.title}</h1>
        {s.subtitle && <p className="text-xl mb-10 opacity-85 max-w-xl mx-auto leading-relaxed">{s.subtitle}</p>}
        {s.cta && (
          <a href={s.ctaLink || '#'}
            className="inline-block rounded-full bg-white text-slate-900 font-bold px-9 py-4 text-base shadow-2xl hover:scale-105 transition-transform">
            {s.cta}
          </a>
        )}
      </div>
    </div>
  );

  if (section.type === 'text') return (
    <div className="max-w-3xl mx-auto py-20 px-6">
      <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">{s.heading}</h2>
      <p className="text-lg text-slate-600 leading-relaxed whitespace-pre-wrap">{s.body}</p>
    </div>
  );

  if (section.type === 'image-text') return (
    <div className="max-w-6xl mx-auto py-20 px-6">
      <div className={`flex flex-col md:flex-row gap-16 items-center ${s.imagePosition === 'right' ? 'md:flex-row-reverse' : ''}`}>
        <img src={s.image} alt="" className="w-full md:w-1/2 rounded-3xl shadow-2xl object-cover aspect-[4/3]" />
        <div className="md:w-1/2">
          <h2 className="text-4xl font-bold text-slate-900 mb-5 leading-tight">{s.heading}</h2>
          <p className="text-lg text-slate-600 leading-relaxed whitespace-pre-wrap">{s.body}</p>
        </div>
      </div>
    </div>
  );

  if (section.type === 'gallery') return (
    <div className="max-w-6xl mx-auto py-20 px-6">
      <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
        {(s.images || []).map((img, i) => (
          <img key={i} src={img} alt="" className="w-full rounded-2xl break-inside-avoid object-cover" />
        ))}
      </div>
    </div>
  );

  if (section.type === 'features') return (
    <div className="max-w-6xl mx-auto py-20 px-6">
      <div className="grid gap-8" style={{ gridTemplateColumns: `repeat(${s.columns || 3}, 1fr)` }}>
        {(s.items || []).map((item, i) => (
          <div key={i} className="text-center p-8 rounded-3xl bg-slate-50 hover:bg-white hover:shadow-xl transition-all duration-300 border border-transparent hover:border-slate-100">
            <div className="text-5xl mb-5">{item.icon}</div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
            <p className="text-slate-500 leading-relaxed">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  );

  if (section.type === 'cta') {
    const bgMap = {
      indigo: 'bg-gradient-to-br from-indigo-600 to-violet-700',
      dark:   'bg-gradient-to-br from-slate-900 to-slate-800',
      gold:   'bg-gradient-to-br from-amber-500 to-orange-600',
      rose:   'bg-gradient-to-br from-rose-500 to-pink-600',
    };
    return (
      <div className={`${bgMap[s.bgStyle] || bgMap.indigo} py-24 px-6 text-center text-white`}>
        <h2 className="text-5xl font-bold mb-4 leading-tight">{s.title}</h2>
        {s.subtitle && <p className="text-xl opacity-85 mb-10 max-w-xl mx-auto">{s.subtitle}</p>}
        {s.buttonText && (
          <a href={s.buttonLink || '#'}
            className="inline-block rounded-full bg-white text-slate-900 font-bold px-9 py-4 text-base shadow-xl hover:scale-105 transition-transform">
            {s.buttonText}
          </a>
        )}
      </div>
    );
  }

  if (section.type === 'marquee') {
    const bgMap = { dark: 'bg-slate-900 text-white', light: 'bg-slate-100 text-slate-700', gold: 'bg-amber-500 text-white' };
    const items = s.items || [];
    const doubled = [...items, ...items, ...items];
    return (
      <div className={`${bgMap[s.style] || bgMap.dark} py-4 overflow-hidden select-none`}>
        <div className="flex gap-12 whitespace-nowrap" style={{ animation: 'marquee 25s linear infinite' }}>
          {doubled.map((item, i) => (
            <span key={i} className="text-sm font-semibold tracking-widest uppercase inline-flex items-center gap-4 flex-shrink-0">
              {item} <span className="opacity-30">✦</span>
            </span>
          ))}
        </div>
        <style>{`@keyframes marquee { from { transform: translateX(0) } to { transform: translateX(-33.333%) } }`}</style>
      </div>
    );
  }

  if (section.type === 'testimonials') return (
    <div className="bg-slate-50 py-20 px-6">
      <div className="max-w-6xl mx-auto">
        {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-14">{s.heading}</h2>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(s.items || []).map((item, i) => (
            <div key={i} className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 flex flex-col gap-4">
              <div className="flex gap-0.5">
                {Array.from({ length: Math.min(Number(item.stars) || 5, 5) }).map((_, si) => (
                  <span key={si} className="text-amber-400 text-lg">★</span>
                ))}
              </div>
              <p className="text-slate-700 leading-relaxed flex-1 text-base italic">"{item.quote}"</p>
              <div>
                <div className="font-bold text-slate-900">{item.name}</div>
                <div className="text-sm text-slate-400">{item.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (section.type === 'product-spotlight') return (
    <div className="max-w-6xl mx-auto py-20 px-6">
      <div className={`flex flex-col md:flex-row gap-16 items-center ${s.layout === 'image-right' ? 'md:flex-row-reverse' : ''}`}>
        <div className="relative md:w-1/2">
          {s.badge && (
            <span className="absolute top-4 left-4 z-10 bg-rose-500 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
              {s.badge}
            </span>
          )}
          <img src={s.image} alt={s.name} className="w-full rounded-3xl shadow-2xl object-cover aspect-square" />
        </div>
        <div className="md:w-1/2 flex flex-col gap-5">
          <h2 className="text-5xl font-bold text-slate-900 leading-tight">{s.name}</h2>
          <p className="text-slate-600 text-lg leading-relaxed">{s.description}</p>
          <div className="flex items-baseline gap-3">
            <span className="text-4xl font-black text-slate-900">{s.price}</span>
            {s.originalPrice && <span className="text-xl text-slate-400 line-through">{s.originalPrice}</span>}
            {s.originalPrice && <span className="text-sm font-bold text-rose-500 bg-rose-50 px-2 py-1 rounded-full">SALE</span>}
          </div>
          {s.buttonText && (
            <a href={s.buttonLink || '#'}
              className="inline-block w-fit rounded-2xl bg-slate-900 text-white font-bold px-10 py-4 text-base hover:bg-slate-700 transition-colors shadow-lg">
              {s.buttonText}
            </a>
          )}
        </div>
      </div>
    </div>
  );

  if (section.type === 'stats') return (
    <div className="bg-gradient-to-br from-slate-900 to-indigo-950 py-20 px-6">
      <div className="max-w-5xl mx-auto">
        {s.heading && <h2 className="text-4xl font-bold text-white text-center mb-14">{s.heading}</h2>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {(s.stats || []).map((stat, i) => (
            <div key={i} className="text-center p-6 rounded-2xl bg-white/5 border border-white/10">
              <div className="text-4xl mb-3">{stat.icon}</div>
              <div className="text-5xl font-black text-white mb-2">{stat.value}</div>
              <div className="text-indigo-300 text-sm font-medium uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (section.type === 'newsletter') {
    if (s.style === 'split') return (
      <div className="bg-indigo-600 py-16 px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-10">
          <div className="text-white md:w-1/2">
            <h2 className="text-3xl font-bold mb-2">{s.heading}</h2>
            {s.subheading && <p className="opacity-80 text-indigo-100">{s.subheading}</p>}
          </div>
          <div className="md:w-1/2 flex w-full gap-3">
            <input type="email" placeholder={s.placeholder || 'Enter your email…'}
              className="flex-1 rounded-2xl px-5 py-4 text-slate-900 outline-none text-sm font-medium" />
            <button className="rounded-2xl bg-white text-indigo-600 font-bold px-7 py-4 text-sm hover:bg-indigo-50 transition whitespace-nowrap">
              {s.buttonText || 'Subscribe'}
            </button>
          </div>
        </div>
      </div>
    );
    return (
      <div className="bg-slate-50 py-20 px-6 text-center">
        <div className="max-w-xl mx-auto">
          <div className="text-5xl mb-6">✉️</div>
          <h2 className="text-4xl font-bold text-slate-900 mb-3">{s.heading}</h2>
          {s.subheading && <p className="text-slate-500 mb-8">{s.subheading}</p>}
          <div className="flex gap-3 max-w-md mx-auto">
            <input type="email" placeholder={s.placeholder || 'Enter your email…'}
              className="flex-1 rounded-2xl border border-slate-200 px-5 py-4 text-sm outline-none focus:ring-2 focus:ring-indigo-400" />
            <button className="rounded-2xl bg-indigo-600 text-white font-bold px-6 py-4 text-sm hover:bg-indigo-700 transition whitespace-nowrap">
              {s.buttonText || 'Subscribe'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (section.type === 'icon-grid') return (
    <div className="max-w-5xl mx-auto py-16 px-6">
      {s.heading && <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">{s.heading}</h2>}
      <div className="grid gap-5" style={{ gridTemplateColumns: `repeat(${s.columns || 4}, 1fr)` }}>
        {(s.items || []).map((item, i) => (
          <div key={i} className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-slate-50 hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-slate-100">
            <span className="text-4xl">{item.icon}</span>
            <span className="text-sm font-semibold text-slate-700 text-center">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );

  if (section.type === 'video-hero') return (
    <div className="relative overflow-hidden bg-black">
      <div className="aspect-video w-full">
        <iframe src={s.videoUrl} className="w-full h-full" frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="section-video" />
      </div>
      {(s.title || s.buttonText) && (
        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-center text-white px-6">
          {s.title && <h2 className="text-5xl font-bold mb-4">{s.title}</h2>}
          {s.subtitle && <p className="text-xl opacity-85 mb-8">{s.subtitle}</p>}
          {s.buttonText && (
            <a href={s.buttonLink || '#'}
              className="inline-block rounded-full bg-white text-slate-900 font-bold px-9 py-4 hover:scale-105 transition-transform">
              {s.buttonText}
            </a>
          )}
        </div>
      )}
    </div>
  );

  if (section.type === 'split-cta') return (
    <div className="grid md:grid-cols-2 min-h-[500px]">
      {[
        { bg: s.leftImage,  heading: s.leftHeading,  sub: s.leftSub,  btn: s.leftBtn,  link: s.leftLink,  grad: 'from-indigo-900 to-indigo-700' },
        { bg: s.rightImage, heading: s.rightHeading, sub: s.rightSub, btn: s.rightBtn, link: s.rightLink, grad: 'from-violet-900 to-purple-700' },
      ].map((panel, i) => (
        <div key={i} className="relative flex items-end p-12 min-h-[400px] overflow-hidden"
          style={{ background: panel.bg
            ? `linear-gradient(to top, rgba(0,0,0,.7) 0%, transparent 60%), url(${panel.bg}) center/cover`
            : undefined
          }}>
          {!panel.bg && <div className={`absolute inset-0 bg-gradient-to-br ${panel.grad}`} />}
          <div className="relative z-10 text-white">
            <h3 className="text-3xl font-bold mb-2">{panel.heading}</h3>
            {panel.sub && <p className="opacity-80 mb-6 text-sm">{panel.sub}</p>}
            {panel.btn && (
              <a href={panel.link || '#'}
                className="inline-block border-2 border-white text-white font-bold px-7 py-3 rounded-full text-sm hover:bg-white hover:text-slate-900 transition-colors">
                {panel.btn}
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  if (section.type === 'countdown') return <CountdownBlock s={s} />;

  if (section.type === 'category-grid') return (
    <div className="max-w-6xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">{s.heading}</h2>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {(s.categories || []).map((cat, i) => (
          <a key={i} href={cat.link || '#'}
            className="group relative rounded-3xl overflow-hidden aspect-[3/4] block shadow-sm hover:shadow-2xl transition-shadow">
            {cat.image
              ? <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              : <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-violet-200" />
            }
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
              <div className="font-bold text-lg">{cat.name}</div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );

  if (section.type === 'before-after') return (
    <div className="max-w-5xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">{s.heading}</h2>}
      <div className="grid md:grid-cols-2 gap-6">
        {[
          { img: s.beforeImage, label: s.beforeLabel || 'Before', color: 'bg-slate-700' },
          { img: s.afterImage,  label: s.afterLabel  || 'After',  color: 'bg-emerald-600' },
        ].map((panel, i) => (
          <div key={i} className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-xl">
            {panel.img
              ? <img src={panel.img} alt={panel.label} className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-slate-200 flex items-center justify-center text-slate-400 text-lg font-semibold">{panel.label}</div>
            }
            <span className={`absolute top-4 left-4 ${panel.color} text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider`}>
              {panel.label}
            </span>
          </div>
        ))}
      </div>
      {s.description && <p className="text-center text-slate-500 mt-8 max-w-lg mx-auto leading-relaxed">{s.description}</p>}
    </div>
  );

  if (section.type === 'faq') return (
    <div className="max-w-3xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">{s.heading}</h2>}
      <div className="flex flex-col gap-3">
        {(s.items || []).map((item, i) => (
          <FAQItem key={i} question={item.question} answer={item.answer} />
        ))}
      </div>
    </div>
  );

  if (section.type === 'team') return (
    <div className="max-w-5xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-3">{s.heading}</h2>}
      {s.subheading && <p className="text-center text-slate-500 mb-12">{s.subheading}</p>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {(s.members || []).map((m, i) => (
          <div key={i} className="text-center">
            {m.photo
              ? <img src={m.photo} alt={m.name} className="w-24 h-24 rounded-full mx-auto mb-4 object-cover ring-4 ring-white shadow-lg" />
              : <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-gradient-to-br from-indigo-200 to-violet-300 flex items-center justify-center text-3xl font-bold text-indigo-700">
                  {m.name?.[0] ?? '?'}
                </div>
            }
            <div className="font-bold text-slate-900">{m.name}</div>
            <div className="text-sm text-slate-500 mt-0.5">{m.role}</div>
          </div>
        ))}
      </div>
    </div>
  );

  if (section.type === 'logo-strip') return (
    <div className="bg-white border-y border-slate-100 py-12 px-6">
      {s.heading && <p className="text-center text-xs font-bold uppercase tracking-widest text-slate-400 mb-8">{s.heading}</p>}
      <div className="flex flex-wrap items-center justify-center gap-10">
        {(s.logos || []).map((logo, i) => (
          <img key={i} src={logo} alt="" className={`h-8 object-contain ${s.style !== 'color' ? 'grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all' : ''}`} />
        ))}
        {(s.logos || []).length === 0 && (
          <p className="text-slate-300 text-sm italic">Add logo URLs in editor</p>
        )}
      </div>
    </div>
  );

  if (section.type === 'steps') return (
    <div className="max-w-5xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-3">{s.heading}</h2>}
      {s.subheading && <p className="text-center text-slate-500 mb-14">{s.subheading}</p>}
      {s.layout === 'vertical' ? (
        <div className="flex flex-col gap-0 max-w-xl mx-auto">
          {(s.steps || []).map((step, i) => (
            <div key={i} className="flex gap-6 relative">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xl font-black shadow-lg flex-shrink-0 z-10">
                  {step.icon || i + 1}
                </div>
                {i < (s.steps.length - 1) && <div className="w-0.5 flex-1 bg-indigo-100 my-2 min-h-[32px]" />}
              </div>
              <div className="pb-10 pt-2">
                <h3 className="text-xl font-bold text-slate-900 mb-1">{step.title}</h3>
                <p className="text-slate-500 leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
          <div className="hidden md:block absolute top-6 left-[16.6%] right-[16.6%] h-0.5 bg-indigo-100 z-0" />
          {(s.steps || []).map((step, i) => (
            <div key={i} className="text-center relative z-10">
              <div className="w-14 h-14 rounded-full bg-indigo-600 text-white flex items-center justify-center text-2xl font-black mx-auto mb-5 shadow-lg">
                {step.icon || i + 1}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
              <p className="text-slate-500 leading-relaxed text-sm">{step.description}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );


  /* ════ NEW 20 RENDERER BLOCKS ════ */

  if (section.type === 'tabs') return <TabbedSection s={s} />;

  if (section.type === 'pricing') return (
    <div className="bg-slate-50 py-20 px-6">
      <div className="max-w-5xl mx-auto">
        {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-3">{s.heading}</h2>}
        {s.subheading && <p className="text-slate-500 text-center mb-14">{s.subheading}</p>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(s.plans||[]).map((plan,i) => (
            <div key={i} className={`rounded-3xl p-8 flex flex-col gap-5 border-2 transition-shadow hover:shadow-xl ${plan.highlighted ? 'bg-indigo-600 border-indigo-600 text-white shadow-2xl scale-105' : 'bg-white border-slate-100 text-slate-900'}`}>
              {plan.highlighted && <span className="text-xs font-bold uppercase tracking-widest bg-white/20 text-white px-3 py-1 rounded-full w-fit">Most Popular</span>}
              <div>
                <div className={`font-bold text-lg mb-1 ${plan.highlighted?'text-white':'text-slate-900'}`}>{plan.name}</div>
                <div className={`text-4xl font-black ${plan.highlighted?'text-white':'text-slate-900'}`}>{plan.price}</div>
              </div>
              <ul className="flex flex-col gap-2.5 flex-1">
                {(plan.features||[]).map((f,fi) => (
                  <li key={fi} className={`flex items-center gap-2 text-sm ${plan.highlighted?'text-indigo-100':'text-slate-600'}`}>
                    <span className={`text-base ${plan.highlighted?'text-white':'text-indigo-500'}`}>✓</span> {f}
                  </li>
                ))}
              </ul>
              <a href="#" className={`block text-center rounded-2xl font-bold py-3.5 text-sm transition-colors ${plan.highlighted ? 'bg-white text-indigo-600 hover:bg-indigo-50' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                {plan.cta || 'Get Started'}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (section.type === 'banner') {
    const bgMap = { indigo:'bg-indigo-600', rose:'bg-rose-500', amber:'bg-amber-500', emerald:'bg-emerald-600', dark:'bg-slate-900' };
    return (
      <div className={`${bgMap[s.style]||bgMap.indigo} text-white py-3 px-6 text-center`}>
        <p className="text-sm font-semibold inline">
          {s.message}
          {s.linkText && <a href={s.linkUrl||'#'} className="ml-3 underline underline-offset-2 font-bold hover:opacity-80 transition">{s.linkText}</a>}
        </p>
      </div>
    );
  }

  if (section.type === 'product-grid') return (
    <div className="max-w-6xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 mb-12 text-center">{s.heading}</h2>}
      <div className="grid gap-6" style={{gridTemplateColumns:`repeat(${s.columns||3},1fr)`}}>
        {(s.products||[]).map((p,i) => (
          <a key={i} href={p.link||'#'} className="group block rounded-3xl overflow-hidden bg-white border border-slate-100 hover:shadow-xl transition-shadow">
            <div className="relative aspect-square overflow-hidden bg-slate-100">
              {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-4xl">🛍️</div>}
              {p.badge && <span className="absolute top-3 left-3 bg-rose-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>}
            </div>
            <div className="p-5">
              <div className="font-semibold text-slate-900 mb-1">{p.name}</div>
              <div className="font-black text-indigo-600">{p.price}</div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );

  if (section.type === 'pullquote') {
    if (s.style === 'left-border') return (
      <div className="max-w-3xl mx-auto py-16 px-6">
        <blockquote className="border-l-4 border-indigo-500 pl-8">
          <p className="text-3xl font-bold text-slate-900 leading-snug mb-6 italic">"{s.quote}"</p>
          <footer><span className="font-bold text-slate-900">{s.name}</span>{s.role && <span className="text-slate-500 ml-2">— {s.role}</span>}</footer>
        </blockquote>
      </div>
    );
    if (s.style === 'dark') return (
      <div className="bg-slate-900 py-20 px-6 text-center text-white">
        <div className="max-w-3xl mx-auto">
          <p className="text-5xl text-indigo-400 font-black mb-6">"</p>
          <p className="text-3xl font-bold leading-snug mb-8 italic">{s.quote}</p>
          <div className="font-bold">{s.name}</div>
          {s.role && <div className="text-slate-400 text-sm mt-1">{s.role}</div>}
        </div>
      </div>
    );
    return (
      <div className="max-w-3xl mx-auto py-20 px-6 text-center">
        <p className="text-6xl text-indigo-200 font-black leading-none mb-4">"</p>
        <p className="text-3xl font-bold text-slate-900 leading-snug mb-6 italic">{s.quote}</p>
        <div className="font-bold text-slate-900">{s.name}</div>
        {s.role && <div className="text-slate-500 text-sm mt-1">{s.role}</div>}
      </div>
    );
  }

  if (section.type === 'bento-grid') return (
    <div className="max-w-6xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">{s.heading}</h2>}
      <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[220px] gap-4">
        {(s.cells||[]).map((cell,i) => (
          <div key={i} className={`relative rounded-3xl overflow-hidden group ${cell.size==='2'?'col-span-2':''}`}>
            {cell.image ? <img src={cell.image} alt={cell.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-violet-200" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
              <div className="font-bold text-lg leading-tight">{cell.title}</div>
              {cell.desc && <div className="text-xs opacity-80 mt-1">{cell.desc}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (section.type === 'timeline') return (
    <div className="max-w-4xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-16">{s.heading}</h2>}
      <div className="relative">
        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-200 -translate-x-1/2 hidden md:block" />
        <div className="flex flex-col gap-10">
          {(s.events||[]).map((ev,i) => (
            <div key={i} className={`flex flex-col md:flex-row gap-6 md:gap-0 items-start ${i%2===0?'md:flex-row':'md:flex-row-reverse'}`}>
              <div className={`md:w-1/2 ${i%2===0?'md:pr-12 md:text-right':'md:pl-12'}`}>
                <div className="inline-block bg-indigo-50 text-indigo-700 font-black text-sm px-3 py-1 rounded-full mb-2">{ev.year}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{ev.title}</h3>
                <p className="text-slate-500 leading-relaxed text-sm">{ev.desc}</p>
              </div>
              <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-indigo-600 ring-4 ring-white shadow-lg" style={{top:`${i*10+3}%`}} />
              <div className="md:w-1/2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (section.type === 'map-embed') return (
    <div className="max-w-6xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">{s.heading}</h2>}
      <div className="grid md:grid-cols-[1fr_360px] gap-8 items-start">
        <div className="rounded-3xl overflow-hidden shadow-xl aspect-video bg-slate-200">
          {s.embedUrl ? <iframe src={s.embedUrl} className="w-full h-full border-0" allowFullScreen title="map" /> : <div className="w-full h-full flex items-center justify-center text-slate-400 text-4xl">🗺️</div>}
        </div>
        <div className="flex flex-col gap-5">
          {[[s.address1,s.address2,'📍'],[s.phone,null,'📞'],[s.email,null,'✉️'],[s.hours,null,'🕐']].filter(([v])=>v).map(([v1,v2,icon],i) => (
            <div key={i} className="flex gap-3">
              <span className="text-xl flex-shrink-0 mt-0.5">{icon}</span>
              <div><div className="text-slate-800 font-medium">{v1}</div>{v2 && <div className="text-slate-500 text-sm">{v2}</div>}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (section.type === 'contact-form') {
    return <ContactFormBlock section={section} />;
  }

  if (section.type === 'comparison') return (
    <div className="max-w-3xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">{s.heading}</h2>}
      <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-sm">
        <div className="grid grid-cols-[1fr_140px_140px] bg-slate-900 text-white">
          <div className="p-5 font-bold text-sm">Feature</div>
          <div className="p-5 font-bold text-sm text-center bg-indigo-600">{s.leftHeader||'Ours'}</div>
          <div className="p-5 font-bold text-sm text-center text-slate-400">{s.rightHeader||'Theirs'}</div>
        </div>
        {(s.rows||[]).map((row,i) => (
          <div key={i} className={`grid grid-cols-[1fr_140px_140px] border-t border-slate-100 ${i%2===0?'bg-white':'bg-slate-50'}`}>
            <div className="p-4 text-sm text-slate-700 font-medium">{row.feature}</div>
            <div className="p-4 text-center bg-indigo-50">
              <span className={`text-lg font-bold ${row.ours==='✓'?'text-emerald-500':'text-rose-500'}`}>{row.ours}</span>
            </div>
            <div className="p-4 text-center">
              <span className={`text-lg font-bold ${row.theirs==='✓'?'text-emerald-500':'text-rose-400'}`}>{row.theirs}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (section.type === 'review-summary') {
    const rating = parseFloat(s.rating||4.8);
    return (
      <div className="max-w-4xl mx-auto py-20 px-6">
        {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">{s.heading}</h2>}
        <div className="grid md:grid-cols-[220px_1fr] gap-12 bg-white rounded-3xl p-10 shadow-sm border border-slate-100">
          <div className="text-center flex flex-col items-center justify-center">
            <div className="text-8xl font-black text-slate-900 leading-none">{s.rating||'4.8'}</div>
            <div className="flex gap-0.5 my-3">{[1,2,3,4,5].map(n=><span key={n} className={`text-2xl ${n<=Math.round(rating)?'text-amber-400':'text-slate-200'}`}>★</span>)}</div>
            <div className="text-slate-500 text-sm">{s.total||'Based on reviews'}</div>
          </div>
          <div className="flex flex-col gap-3 justify-center">
            {[5,4,3,2,1].map(star => {
              const pct = parseInt((s.breakdown||{})[star]||0);
              return (
                <div key={star} className="flex items-center gap-3">
                  <span className="text-sm text-amber-500 font-bold w-6">{star}★</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                    <div className="h-full bg-amber-400 rounded-full transition-all" style={{width:`${pct}%`}} />
                  </div>
                  <span className="text-xs text-slate-400 w-8 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (section.type === 'accordion') return <AccordionSection s={s} />;

  if (section.type === 'social-feed') return (
    <div className="max-w-5xl mx-auto py-20 px-6">
      <div className="text-center mb-10">
        {s.heading && <h2 className="text-3xl font-bold text-slate-900 mb-2">{s.heading}</h2>}
        {s.handle && <a href={s.profileLink||'#'} className="text-indigo-600 font-semibold hover:underline">{s.handle}</a>}
      </div>
      <div className="grid grid-cols-4 gap-3">
        {(s.posts||[]).slice(0,8).map((post,i) => (
          <a key={i} href={s.profileLink||'#'} className="group relative aspect-square rounded-2xl overflow-hidden bg-slate-100 block">
            <img src={post} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
              <span className="text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity">♥</span>
            </div>
          </a>
        ))}
      </div>
      {s.handle && <div className="text-center mt-8"><a href={s.profileLink||'#'} className="inline-block rounded-full border-2 border-slate-900 text-slate-900 font-bold px-8 py-3 text-sm hover:bg-slate-900 hover:text-white transition-colors">Follow {s.handle}</a></div>}
    </div>
  );

  if (section.type === 'hero-slider') return <HeroSlider s={s} />;

  if (section.type === 'trust-badges') {
    const badges = s.badges||[];
    if (s.layout==='cards') return (
      <div className="max-w-5xl mx-auto py-16 px-6">
        {s.heading && <h2 className="text-2xl font-bold text-slate-900 text-center mb-10">{s.heading}</h2>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {badges.map((b,i) => (
            <div key={i} className="flex flex-col items-center gap-2 p-6 rounded-2xl border-2 border-slate-100 bg-white text-center hover:border-indigo-200 hover:shadow-md transition-all">
              <span className="text-4xl">{b.icon}</span>
              <div className="font-bold text-slate-900 text-sm">{b.title}</div>
              <div className="text-xs text-slate-500">{b.subtitle}</div>
            </div>
          ))}
        </div>
      </div>
    );
    return (
      <div className="border-y border-slate-100 py-8 px-6 bg-white">
        {s.heading && <p className="text-center text-xs font-bold uppercase tracking-widest text-slate-400 mb-6">{s.heading}</p>}
        <div className="flex flex-wrap items-center justify-center gap-10">
          {badges.map((b,i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-2xl">{b.icon}</span>
              <div><div className="font-bold text-slate-900 text-sm">{b.title}</div><div className="text-xs text-slate-500">{b.subtitle}</div></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (section.type === 'guarantee') {
    const themes = {
      light: { wrap:'bg-white border border-slate-200', text:'text-slate-900', sub:'text-slate-500', badge:'bg-indigo-50 text-indigo-700' },
      dark:  { wrap:'bg-slate-900', text:'text-white', sub:'text-slate-400', badge:'bg-white/10 text-white' },
      gold:  { wrap:'bg-gradient-to-br from-amber-500 to-orange-600', text:'text-white', sub:'text-amber-100', badge:'bg-white/20 text-white' },
    };
    const t = themes[s.style]||themes.light;
    return (
      <div className={`${t.wrap} rounded-none py-20 px-6`}>
        <div className="max-w-3xl mx-auto text-center">
          <div className={`inline-flex items-center gap-3 ${t.badge} rounded-full px-6 py-3 text-sm font-bold mb-8`}>
            <span className="text-2xl">{s.icon||'🛡️'}</span> {s.label||'Guarantee'}
          </div>
          <h2 className={`text-4xl font-bold ${t.text} mb-5`}>{s.heading}</h2>
          <p className={`${t.sub} text-lg leading-relaxed mb-10`}>{s.body}</p>
          {s.buttonText && <a href={s.buttonLink||'#'} className={`inline-block rounded-full font-bold px-9 py-4 text-base transition ${s.style==='light'?'bg-indigo-600 text-white hover:bg-indigo-700':'bg-white text-slate-900 hover:opacity-90'}`}>{s.buttonText}</a>}
        </div>
      </div>
    );
  }

  if (section.type === 'press') return (
    <div className="bg-slate-50 py-20 px-6">
      <div className="max-w-5xl mx-auto">
        {s.heading && <p className="text-center text-xs font-bold uppercase tracking-widest text-slate-400 mb-12">{s.heading}</p>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(s.items||[]).map((item,i) => (
            <div key={i} className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
              <p className="text-slate-600 italic leading-relaxed mb-6 text-lg">"{item.quote}"</p>
              <div className="flex items-center gap-3">
                {item.logo ? <img src={item.logo} alt={item.publication} className="h-6 object-contain grayscale" /> : <span className="font-black text-slate-900 text-sm">{item.publication}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (section.type === 'ambassador') return (
    <div className="max-w-5xl mx-auto py-20 px-6">
      {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-3">{s.heading}</h2>}
      {s.subheading && <p className="text-slate-500 text-center mb-12">{s.subheading}</p>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {(s.people||[]).map((p,i) => (
          <div key={i} className="text-center group">
            <div className="relative inline-block mb-4">
              {p.photo ? <img src={p.photo} alt={p.name} className="w-28 h-28 rounded-full object-cover mx-auto ring-4 ring-white shadow-lg" /> : <div className="w-28 h-28 rounded-full bg-gradient-to-br from-pink-200 to-purple-300 flex items-center justify-center text-3xl mx-auto shadow-lg">{p.name?.[0]||'?'}</div>}
              <span className="absolute -bottom-1 -right-1 bg-gradient-to-br from-pink-500 to-orange-400 text-white text-xs font-bold px-2 py-0.5 rounded-full">{p.followers}</span>
            </div>
            <div className="font-bold text-slate-900">{p.name}</div>
            <div className="text-sm text-indigo-500">{p.handle}</div>
          </div>
        ))}
      </div>
    </div>
  );

  if (section.type === 'size-guide') {
    const headers = (s.headers||'').split(',').map(h=>h.trim()).filter(Boolean);
    const rows = s.rows||[];
    return (
      <div className="max-w-4xl mx-auto py-20 px-6">
        {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-3">{s.heading}</h2>}
        {s.subheading && <p className="text-slate-500 text-center mb-10">{s.subheading}</p>}
        <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-sm">
          {headers.length > 0 && (
            <div className="grid bg-slate-900 text-white" style={{gridTemplateColumns:`repeat(${headers.length},1fr)`}}>
              {headers.map((h,i) => <div key={i} className="p-4 text-xs font-bold uppercase tracking-wider text-center">{h}</div>)}
            </div>
          )}
          {rows.map((row,i) => {
            const cells = row.split(',').map(c=>c.trim());
            return (
              <div key={i} className={`grid border-t border-slate-100 ${i%2===0?'bg-white':'bg-slate-50'}`} style={{gridTemplateColumns:`repeat(${Math.max(headers.length,cells.length)},1fr)`}}>
                {cells.map((cell,ci) => <div key={ci} className={`p-4 text-sm text-center ${ci===0?'font-bold text-slate-900':'text-slate-600'}`}>{cell}</div>)}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (section.type === 'gift-cards') {
    const amts = (s.amounts||'$25, $50, $100, $200').split(',').map(a=>a.trim()).filter(Boolean);
    return (
      <div className="max-w-5xl mx-auto py-20 px-6">
        {s.heading && <h2 className="text-4xl font-bold text-slate-900 text-center mb-3">{s.heading}</h2>}
        {s.subheading && <p className="text-slate-500 text-center mb-14">{s.subheading}</p>}
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="relative">
            {s.cardImage ? <img src={s.cardImage} alt="Gift Card" className="w-full rounded-3xl shadow-2xl" /> : (
              <div className="w-full aspect-[16/10] rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-700 shadow-2xl flex items-center justify-center">
                <div className="text-center text-white"><div className="text-6xl mb-3">🎁</div><div className="text-2xl font-bold">Gift Card</div></div>
              </div>
            )}
          </div>
          <div>
            <p className="text-slate-600 leading-relaxed mb-8">{s.description}</p>
            <div className="flex flex-wrap gap-3 mb-8">
              {amts.map((amt,i) => (
                <button key={i} className={`rounded-xl border-2 px-5 py-2.5 text-sm font-bold transition-colors ${i===1?'bg-indigo-600 border-indigo-600 text-white':'border-slate-200 text-slate-700 hover:border-indigo-400'}`}>{amt}</button>
              ))}
            </div>
            {s.buttonText && <a href={s.buttonLink||'#'} className="inline-block rounded-2xl bg-slate-900 text-white font-bold px-8 py-4 text-sm hover:bg-slate-700 transition-colors shadow-lg">{s.buttonText}</a>}
          </div>
        </div>
      </div>
    );
  }

  if (section.type === 'policy-page') {
    const alertColors = {
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      blue:   'bg-blue-50 border-blue-200 text-blue-800',
      green:  'bg-emerald-50 border-emerald-200 text-emerald-800',
      red:    'bg-red-50 border-red-200 text-red-800',
    };
    const ac = alertColors[s.alertColor || 'orange'];
    return (
      <div className="max-w-3xl mx-auto py-16 px-6">
        <div className="text-center mb-12">
          {s.icon && <div className="text-5xl mb-4">{s.icon}</div>}
          <h1 className="text-4xl font-bold text-slate-900 mb-2">{s.title}</h1>
          {s.lastUpdated && <p className="text-sm text-slate-400">Last updated: {s.lastUpdated}</p>}
        </div>
        {s.alertText && <div className={`border rounded-2xl p-5 text-sm font-medium mb-8 ${ac}`}>{s.alertText}</div>}
        <div className="flex flex-col gap-8">
          {(s.items || []).map((item, i) => (
            <div key={i}>
              <h2 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h2>
              <p className="text-slate-600 leading-relaxed text-sm">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (section.type === 'about-story') {
    const paragraphs = (s.body || '').split(/\n\n+/).filter(Boolean);
    const imgEl = s.image && (
      <div className="relative">
        <img src={s.image} alt="" className="rounded-3xl shadow-2xl w-full object-cover" />
        {s.yearBadge && (
          <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-6 hidden md:block border border-gray-100">
            <div className="font-bold text-3xl text-orange-500">{s.yearBadge}</div>
            {s.yearLabel && <div className="text-sm text-slate-500">{s.yearLabel}</div>}
          </div>
        )}
      </div>
    );
    const textEl = (
      <div>
        {s.eyebrow && <p className="text-sm font-bold text-orange-500 uppercase tracking-wider mb-3">{s.eyebrow}</p>}
        <h2 className="text-4xl font-bold text-slate-900 mb-5 leading-tight">{s.heading}</h2>
        {paragraphs.map((p, i) => <p key={i} className="text-slate-500 leading-relaxed mb-4">{p}</p>)}
        {s.ctaText && (
          <a href={s.ctaLink || '#'} className="inline-block mt-3 bg-orange-500 text-white font-bold px-8 py-4 rounded-2xl hover:bg-orange-600 transition-colors">
            {s.ctaText}
          </a>
        )}
      </div>
    );
    return (
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {s.imagePosition === 'left' ? <>{imgEl}{textEl}</> : <>{textEl}{imgEl}</>}
        </div>
      </div>
    );
  }

  if (section.type === 'dark-stats') return (
    <div className="bg-gray-900 py-16 px-6">
      {s.heading && <h2 className="text-3xl font-bold text-white text-center mb-12">{s.heading}</h2>}
      <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        {(s.items || []).map((item, i) => (
          <div key={i}>
            <div className="text-4xl font-black text-orange-400 mb-1">{item.value}</div>
            <div className="font-bold text-white text-sm mb-1">{item.label}</div>
            {item.sub && <div className="text-xs text-slate-500">{item.sub}</div>}
          </div>
        ))}
      </div>
    </div>
  );

  if (section.type === 'contact-info') return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      {s.heading && <h2 className="text-3xl font-bold text-slate-900 mb-8">{s.heading}</h2>}
      <div className="flex flex-col gap-4 mb-6">
        {(s.items || []).map((item, i) => (
          <div key={i} className="flex gap-4 bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
            <span className="text-2xl flex-shrink-0">{item.icon}</span>
            <div>
              <p className="font-bold text-slate-900 text-sm">{item.label}</p>
              <p className="text-slate-500 text-sm leading-relaxed">{item.value}</p>
            </div>
          </div>
        ))}
      </div>
      {s.corporateHeading && (
        <div className="bg-orange-50 rounded-2xl p-6 border border-orange-100">
          <h3 className="font-bold text-slate-900 mb-2">{s.corporateHeading}</h3>
          {s.corporateBody && <p className="text-sm text-slate-500 mb-4">{s.corporateBody}</p>}
          {s.corporateBtnText && (
            <a href={s.corporateBtnLink || '#'} className="inline-block bg-orange-500 text-white font-bold px-5 py-2.5 rounded-xl text-sm hover:bg-orange-600 transition-colors">
              {s.corporateBtnText}
            </a>
          )}
        </div>
      )}
    </div>
  );

  return null;
}

/* ═══════════════════════════════════════════
   CUSTOM PAGE RENDERER
═══════════════════════════════════════════ */
function CustomPageRenderer({ page }) {
  return (
    <div className="font-sans">
      {page.sections.map(section => (
        <SectionBlock key={section.id} section={section} />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════
   PUBLIC PAGE (customer-facing, no AdminProvider needed)
═══════════════════════════════════════════ */
export function PublicCustomPage() {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | found | notfound

  useEffect(() => {
    setStatus('loading');
    axios.get(`${apiUrl}/pages`)
      .then(res => {
        const list = res.data?.list || [];
        const found = list.find(p => p.slug === slug && p.status === 'published');
        if (found) {
          const sections = Array.isArray(found.sections) ? found.sections
            : (typeof found.sections === 'string' ? JSON.parse(found.sections || '[]') : []);
          setPageData({ ...found, sections });
          setStatus('found');
        } else {
          setStatus('notfound');
        }
      })
      .catch(() => setStatus('notfound'));
  }, [slug]);

  if (status === 'loading') return (
    <div className="flex items-center justify-center py-40 text-slate-400 text-sm">Loading…</div>
  );
  if (status === 'notfound') return (
    <div className="text-center py-32 px-6">
      <div className="text-6xl mb-4">🔍</div>
      <h2 className="text-4xl font-bold text-slate-900">Page Not Found</h2>
      <p className="text-slate-500 mt-3">No published page exists at <code>/{slug}</code></p>
    </div>
  );
  return <CustomPageRenderer page={pageData} />;
}


const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/* ═══════════════════════════════════════════
   CUSTOM PAGE VIEWER (named export)
═══════════════════════════════════════════ */
export function CustomPageViewer() {
  const { customPages, page } = useAdmin();
  const slug = page.replace('custom-', '');
  const pageData = customPages.find(p => p.slug === slug && p.status === 'published');
  if (!pageData) return (
    <div className="text-center py-32 px-6">
      <div className="text-6xl mb-4">🔍</div>
      <h2 className="text-4xl font-bold text-slate-900">Page Not Found</h2>
    </div>
  );
  return <CustomPageRenderer page={pageData} />;
}

/* ═══════════════════════════════════════════
   SECTION DEFAULTS (21 types)
═══════════════════════════════════════════ */
const SECTION_DEFAULTS = {
  hero:                { title: 'Welcome to Our Store', subtitle: 'Discover something beautiful today', cta: 'Shop Now', ctaLink: '/shop', image: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1400&q=80' },
  text:                { heading: 'Our Story', body: 'Tell your brand story here. Share your mission, values, and what makes you unique...' },
  'image-text':        { heading: 'Why Choose Us', body: 'Share what makes your brand special and why customers love shopping with you.', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80', imagePosition: 'left' },
  gallery:             { images: ['https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=600&q=80','https://images.unsplash.com/photo-1576995853123-5a10305d93c0?w=600&q=80','https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=600&q=80','https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?w=600&q=80'] },
  features:            { columns: 3, items: [{ icon: '🚚', title: 'Free Shipping', text: 'On all orders over $50' },{ icon: '🔄', title: 'Easy Returns', text: '30-day hassle-free returns' },{ icon: '🛡️', title: 'Secure Checkout', text: 'SSL encrypted payments' }] },
  cta:                 { title: 'Ready to Shop?', subtitle: 'Join 50,000+ happy customers', buttonText: 'Explore Collection', buttonLink: '/shop', bgStyle: 'indigo' },
  marquee:             { items: ['Free Shipping Over $50','New Arrivals Every Week','30-Day Returns','Secure Checkout','50K+ Happy Customers','Handcrafted with Love'], style: 'dark' },
  testimonials:        { heading: 'What Our Customers Say', items: [{ name: 'Sarah M.', role: 'Verified Buyer', quote: 'Absolutely love the quality! Will definitely order again.', stars: 5 },{ name: 'James R.', role: 'Verified Buyer', quote: 'Fast shipping and beautiful packaging. 10/10!', stars: 5 },{ name: 'Priya K.', role: 'Verified Buyer', quote: 'The product exceeded my expectations completely.', stars: 5 }] },
  'product-spotlight': { badge: 'Best Seller', name: 'Signature Fragrance', description: 'A luxurious blend crafted for those who demand the extraordinary. Notes of cedar, amber, and oud that linger long after.', price: '$89.00', originalPrice: '$129.00', image: 'https://images.unsplash.com/photo-1563170351-be82bc888aa4?w=800&q=80', buttonText: 'Add to Cart', buttonLink: '/product/signature', layout: 'image-left' },
  stats:               { heading: 'Trusted by Thousands', stats: [{ value: '50K+', label: 'Happy Customers', icon: '😊' },{ value: '4.9★', label: 'Average Rating', icon: '⭐' },{ value: '200+', label: 'Products', icon: '📦' },{ value: '99%', label: 'Satisfaction', icon: '✅' }] },
  newsletter:          { heading: 'Stay in the Loop', subheading: 'Get exclusive deals and new arrival alerts. No spam, ever.', placeholder: 'Enter your email…', buttonText: 'Subscribe', style: 'centered' },
  'icon-grid':         { heading: 'Why Shop With Us', columns: 4, items: [{ icon: '🚚', label: 'Free Delivery' },{ icon: '💳', label: 'Secure Payment' },{ icon: '🎁', label: 'Gift Wrapping' },{ icon: '🔄', label: 'Easy Returns' },{ icon: '🌿', label: 'Eco-Friendly' },{ icon: '📞', label: '24/7 Support' },{ icon: '✨', label: 'Premium Quality' },{ icon: '🏷️', label: 'Best Prices' }] },
  'video-hero':        { videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ', title: 'See It in Action', subtitle: 'Watch how our products transform everyday moments', buttonText: 'Shop the Look', buttonLink: '/shop' },
  'split-cta':         { leftHeading: "Women's Collection", leftSub: 'New arrivals just dropped', leftBtn: 'Shop Women', leftLink: '/women', leftImage: 'https://images.unsplash.com/photo-1581044777550-4cfa60707c03?w=800&q=80', rightHeading: "Men's Collection", rightSub: 'Timeless essentials redefined', rightBtn: 'Shop Men', rightLink: '/men', rightImage: 'https://images.unsplash.com/photo-1490114538077-0a7f8cb49891?w=800&q=80' },
  countdown:           { heading: 'Sale Ends In', subheading: 'Up to 50% off — limited time only. Don\'t miss out!', targetDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), buttonText: 'Shop the Sale', buttonLink: '/sale', style: 'dark' },
  'category-grid':     { heading: 'Shop by Category', categories: [{ name: 'Tops', image: 'https://images.unsplash.com/photo-1618354691373-d851c5c3a990?w=600&q=80', link: '/tops' },{ name: 'Bottoms', image: 'https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?w=600&q=80', link: '/bottoms' },{ name: 'Dresses', image: 'https://images.unsplash.com/photo-1612336307429-8a898d10e223?w=600&q=80', link: '/dresses' },{ name: 'Accessories', image: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=600&q=80', link: '/accessories' }] },
  'before-after':      { heading: 'The Difference Is Clear', beforeImage: 'https://images.unsplash.com/photo-1595433707802-6b2626ef1c91?w=700&q=80', beforeLabel: 'Before', afterImage: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=700&q=80', afterLabel: 'After', description: 'Real results from real customers. See the transformation for yourself.' },
  faq:                 { heading: 'Frequently Asked Questions', items: [{ question: 'How long does shipping take?', answer: 'Standard shipping takes 3–5 business days. Express options are available at checkout.' },{ question: 'What is your return policy?', answer: 'We offer a 30-day hassle-free return policy on all unused items in original packaging.' },{ question: 'Do you ship internationally?', answer: 'Yes! We ship to over 50 countries. International shipping rates apply at checkout.' },{ question: 'How can I track my order?', answer: 'You will receive a tracking email within 24 hours of your order being dispatched.' }] },
  team:                { heading: 'Meet the Team', subheading: 'The passionate people behind the magic', members: [{ name: 'Alex Kim', role: 'Founder & CEO', photo: '' },{ name: 'Maria Chen', role: 'Head of Design', photo: '' },{ name: 'Sam Patel', role: 'Lead Developer', photo: '' },{ name: 'Lily Rose', role: 'Customer Success', photo: '' }] },
  'logo-strip':        { heading: 'As Featured In', logos: [], style: 'grayscale' },
  steps:               { heading: 'How It Works', subheading: 'Three simple steps to get started today', layout: 'horizontal', steps: [{ icon: '🛍️', title: 'Browse & Choose', description: 'Explore thousands of curated products and find your perfect match.' },{ icon: '💳', title: 'Secure Checkout', description: 'Pay safely with our encrypted checkout in just seconds.' },{ icon: '📦', title: 'Fast Delivery', description: 'Receive your order within 3–5 days with real-time tracking.' }] },
};

const BLANK = { title: '', slug: '', navTitle: '', showInNav: true, status: 'published', sections: [] };

const SECTION_BTNS = [
  ['Hero',              'hero'],
  ['Text Block',        'text'],
  ['Image + Text',      'image-text'],
  ['Gallery',           'gallery'],
  ['Features',          'features'],
  ['CTA Banner',        'cta'],
  ['Marquee Ticker',    'marquee'],
  ['Testimonials',      'testimonials'],
  ['Product Spotlight', 'product-spotlight'],
  ['Stats Bar',         'stats'],
  ['Newsletter',        'newsletter'],
  ['Icon Grid',         'icon-grid'],
  ['Video Hero',        'video-hero'],
  ['Split CTA',         'split-cta'],
  ['Countdown Timer',   'countdown'],
  ['Category Grid',     'category-grid'],
  ['Before & After',    'before-after'],
  ['FAQ',               'faq'],
  ['Team',              'team'],
  ['Logo Strip',        'logo-strip'],
  ['How It Works',      'steps'],
  ['Tabbed Content',    'tabs'],
  ['Pricing Table',     'pricing'],
  ['Announcement Bar',  'banner'],
  ['Product Grid',      'product-grid'],
  ['Pull Quote',        'pullquote'],
  ['Bento Grid',        'bento-grid'],
  ['Timeline',          'timeline'],
  ['Map & Location',    'map-embed'],
  ['Contact Form',      'contact-form'],
  ['Comparison Table',  'comparison'],
  ['Review Summary',    'review-summary'],
  ['Accordion',         'accordion'],
  ['Social Feed',       'social-feed'],
  ['Hero Slider',       'hero-slider'],
  ['Trust Badges',      'trust-badges'],
  ['Guarantee',         'guarantee'],
  ['Press Quotes',      'press'],
  ['Ambassadors',       'ambassador'],
  ['Size Guide',        'size-guide'],
  ['Gift Cards',        'gift-cards'],
  ['Policy Page',       'policy-page'],
  ['About Story',       'about-story'],
  ['Dark Stats',        'dark-stats'],
  ['Contact Info',      'contact-info'],
];


/* patch SECTION_DEFAULTS with 20 new entries — spread over existing object at runtime
   We define them as a separate const and merge in DynamicPages */
const EXTRA_DEFAULTS = {
  tabs:             { heading: 'Everything You Need', tabs: [{ label: 'Quality', content: 'We use only the finest materials, ethically sourced and rigorously tested before reaching you.', image: '' }, { label: 'Sustainability', content: 'Every product is crafted with the environment in mind — from recycled packaging to carbon-neutral shipping.', image: '' }, { label: 'Community', content: 'We give 1% of every sale to reforestation projects and local artisan cooperatives.', image: '' }] },
  pricing:          { heading: 'Simple, Transparent Pricing', subheading: 'No hidden fees. Cancel anytime.', plans: [{ name: 'Starter', price: 'Free', cta: 'Get Started', features: ['Up to 5 products', 'Basic analytics', 'Email support'], highlighted: false }, { name: 'Pro', price: '$29/mo', cta: 'Start Free Trial', features: ['Unlimited products', 'Advanced analytics', 'Priority support', 'Custom domain'], highlighted: true }, { name: 'Enterprise', price: '$99/mo', cta: 'Contact Sales', features: ['Everything in Pro', 'Dedicated account manager', 'Custom integrations', 'SLA guarantee'], highlighted: false }] },
  banner:           { message: '🎉 Summer Sale — Up to 50% off everything!', linkText: 'Shop Now →', linkUrl: '/sale', style: 'rose' },
  'product-grid':   { heading: 'Featured Products', columns: 3, products: [{ name: 'Classic Tee', price: '$34.00', image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600&q=80', link: '/product/classic-tee', badge: 'New' }, { name: 'Linen Pants', price: '$79.00', image: 'https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?w=600&q=80', link: '/product/linen-pants', badge: '' }, { name: 'Silk Blouse', price: '$95.00', image: 'https://images.unsplash.com/photo-1618354691373-d851c5c3a990?w=600&q=80', link: '/product/silk-blouse', badge: 'Sale' }] },
  pullquote:        { quote: 'The details are not the details. They make the design.', name: 'Charles Eames', role: 'Designer', style: 'centered' },
  'bento-grid':     { heading: 'Crafted With Purpose', cells: [{ title: 'Sustainable Materials', desc: 'Ethically sourced from day one', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80', size: '2' }, { title: 'Handcrafted Quality', desc: 'Made by master artisans', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&q=80', size: '1' }, { title: 'Global Reach', desc: 'Shipped to 50+ countries', image: 'https://images.unsplash.com/photo-1494412651409-8963ce7935a7?w=600&q=80', size: '1' }, { title: 'Community First', desc: 'Every purchase gives back', image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&q=80', size: '1' }, { title: 'Innovation Lab', desc: 'Always pushing boundaries', image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&q=80', size: '1' }] },
  timeline:         { heading: 'Our Journey', events: [{ year: '2018', title: 'Founded', desc: 'Started in a small studio with a big dream — to make quality accessible to everyone.' }, { year: '2020', title: '10K Customers', desc: 'Reached our first major milestone, serving customers across 20 countries.' }, { year: '2022', title: 'B Corp Certified', desc: 'Became a certified B Corporation, cementing our commitment to people and planet.' }, { year: '2024', title: 'Global Expansion', desc: 'Opened flagship stores in New York, London, and Tokyo.' }] },
  'map-embed':      { heading: 'Visit Our Store', embedUrl: '', address1: '123 Fashion Avenue', address2: 'New York, NY 10001', phone: '+1 (212) 555-0199', email: 'hello@yourstore.com', hours: 'Mon–Sat 10am–8pm, Sun 12pm–6pm' },
  'contact-form':   { heading: 'Get in Touch', subheading: "We'd love to hear from you. Send us a message and we'll respond within 24 hours.", buttonText: 'Send Message', style: 'split', contactEmail: 'hello@yourstore.com', contactPhone: '+1 (212) 555-0199' },
  comparison:       { heading: 'Why Choose Us?', leftHeader: 'Our Brand', rightHeader: 'Others', rows: [{ feature: 'Ethically Sourced Materials', ours: '✓', theirs: '✗' }, { feature: 'Carbon Neutral Shipping', ours: '✓', theirs: '✗' }, { feature: '30-Day Free Returns', ours: '✓', theirs: '✗' }, { feature: 'Lifetime Repair Guarantee', ours: '✓', theirs: '✗' }, { feature: '24/7 Customer Support', ours: '✓', theirs: '✗' }] },
  'review-summary': { heading: 'Customer Reviews', rating: '4.8', total: '2,847 verified reviews', breakdown: { 5: 76, 4: 15, 3: 5, 2: 2, 1: 2 } },
  accordion:        { heading: 'Product Details', style: 'default', items: [{ title: 'Materials & Care', content: 'Made from 100% organic cotton. Machine wash cold, tumble dry low. Do not bleach.' }, { title: 'Sizing & Fit', content: 'This style runs true to size. We recommend sizing up if you prefer a relaxed fit. See our full size guide below.' }, { title: 'Shipping & Returns', content: 'Free standard shipping on orders over $50. Returns accepted within 30 days of delivery.' }, { title: 'Sustainability', content: 'Produced in a Fair Trade certified factory. Packaging is 100% recycled and biodegradable.' }] },
  'social-feed':    { heading: 'Follow Our Journey', handle: '@yourbrand', profileLink: 'https://instagram.com', posts: ['https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=400&q=80','https://images.unsplash.com/photo-1576995853123-5a10305d93c0?w=400&q=80','https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400&q=80','https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?w=400&q=80','https://images.unsplash.com/photo-1581044777550-4cfa60707c03?w=400&q=80','https://images.unsplash.com/photo-1490114538077-0a7f8cb49891?w=400&q=80','https://images.unsplash.com/photo-1618354691373-d851c5c3a990?w=400&q=80','https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?w=400&q=80'] },
  'hero-slider':    { slides: [{ title: 'New Season Arrivals', subtitle: 'Fresh styles for every occasion', cta: 'Shop Now', link: '/new', image: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1400&q=80' }, { title: "Women's Edit", subtitle: 'Curated looks for the modern woman', cta: 'Explore', link: '/women', image: 'https://images.unsplash.com/photo-1581044777550-4cfa60707c03?w=1400&q=80' }, { title: 'The Sale Is On', subtitle: 'Up to 50% off selected styles', cta: 'Shop Sale', link: '/sale', image: 'https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1400&q=80' }] },
  'trust-badges':   { heading: '', layout: 'cards', badges: [{ icon: '🚚', title: 'Free Shipping', subtitle: 'On orders over $50' }, { icon: '🔄', title: 'Easy Returns', subtitle: '30-day hassle-free' }, { icon: '🔒', title: 'Secure Payment', subtitle: 'SSL encrypted checkout' }, { icon: '🌿', title: 'Eco-Friendly', subtitle: 'Carbon neutral delivery' }] },
  guarantee:        { heading: 'Our Promise to You', body: "If you're not completely satisfied with your purchase for any reason, simply return it within 30 days for a full refund — no questions asked. We stand behind every product we make.", icon: '🛡️', label: '30-Day Money Back Guarantee', buttonText: 'Learn More', buttonLink: '/returns', style: 'light' },
  press:            { heading: 'As Seen In', items: [{ publication: 'Vogue', logo: '', quote: '"The brand redefining sustainable luxury for a new generation of conscious consumers."' }, { publication: 'Forbes', logo: '', quote: '"A masterclass in how to build a modern brand that genuinely walks its talk."' }, { publication: 'GQ', logo: '', quote: '"Effortless quality that makes getting dressed feel like a pleasure, not a chore."' }] },
  ambassador:       { heading: 'Our Ambassadors', subheading: 'Real people who love what we do', people: [{ name: 'Emma Liu', handle: '@emmaliu', followers: '240K', photo: '' }, { name: 'Jake Torres', handle: '@jaketorres', followers: '180K', photo: '' }, { name: 'Nia Adeyemi', handle: '@niaadeyemi', followers: '95K', photo: '' }, { name: 'Sam Park', handle: '@sampark', followers: '310K', photo: '' }] },
  'size-guide':     { heading: 'Size Guide', subheading: 'Find your perfect fit with our easy-to-use size chart', headers: 'Size, Chest (in), Waist (in), Hip (in)', rows: ['XS, 32–34, 24–26, 34–36', 'S, 34–36, 26–28, 36–38', 'M, 36–38, 28–30, 38–40', 'L, 38–40, 30–32, 40–42', 'XL, 40–42, 32–34, 42–44'] },
  'gift-cards':     { heading: 'Give the Gift of Choice', subheading: 'Perfect for every occasion — delivered instantly by email', cardImage: '', description: 'Not sure what to get? A gift card lets your loved one choose exactly what they want. Redeemable online and in-store, valid for 3 years, never expires.', buttonText: 'Buy a Gift Card', buttonLink: '/gift-cards', amounts: '$25, $50, $100, $200' },
  'policy-page':    { title: 'Privacy Policy', icon: '🔒', lastUpdated: 'January 1, 2025', alertText: 'Your privacy is our priority. We collect only what we need and never sell your data.', alertColor: 'orange', items: [{ title: '1. Information We Collect', body: 'We collect your name, email, phone, delivery address and order history when you register or place an order.' }, { title: '2. How We Use Your Information', body: 'Your data is used to process orders, send updates, and personalise your experience.' }, { title: '3. Your Rights', body: 'You can request access, correction or deletion of your data at any time by emailing us.' }] },
  'about-story':    { eyebrow: 'The Beginning', heading: 'From Grandmother\'s Kitchen to 10,000 Customers', body: 'We started as a small tiffin service in 2020 with a dream to bring authentic home cooking to everyone.\n\nToday we serve thousands of customers monthly, all while maintaining the same family recipe standards that started it all.', ctaText: 'Order Now', ctaLink: '/menu', image: 'https://images.unsplash.com/photo-1564671165093-20688ff1fffa?w=700&q=80', imagePosition: 'right', yearBadge: '2020', yearLabel: 'Founded' },
  'dark-stats':     { heading: '', items: [{ value: '50+', label: 'Menu Dishes', sub: 'and growing' }, { value: '10K+', label: 'Monthly Customers', sub: 'since 2020' }, { value: '4.9★', label: 'Average Rating', sub: 'from 8,000+ reviews' }, { value: '30min', label: 'Delivery Target', sub: 'or it\'s free' }] },
  'contact-info':   { heading: 'Contact Info', items: [{ icon: '📍', label: 'Address', value: '123 Food Street, Hyderabad, TS 500034' }, { icon: '📞', label: 'Phone', value: '+91 98765 43210' }, { icon: '✉️', label: 'Email', value: 'hello@example.com' }, { icon: '⏰', label: 'Hours', value: 'Mon–Sun: 7:00 AM – 11:00 PM' }], corporateHeading: 'Corporate Catering', corporateBody: 'Feeding your team? We do office catering for 10–500 people with custom menus.', corporateBtnText: 'Enquire Now', corporateBtnLink: '/contact' },
};

const SectionCard = ({ title, children, action, subtitle }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="flex items-start justify-between px-6 py-4 border-b border-gray-50">
      <div>
        <h2 className="text-base font-bold text-gray-800">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="p-6">{children}</div>
  </div>
);


export default function DynamicPages() {
  const { customPages, loadCustomPages, saveCustomPage, deleteCustomPage, orders } = useAdmin();
  const [showEditor, setShowEditor]           = useState(false);
  const [editing, setEditing]                 = useState(null);
  const [form, setForm]                       = useState(BLANK);
  const [previewMode, setPreviewMode]         = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [sectionSearch, setSectionSearch]     = useState('');
  const [saving, setSaving]                   = useState(false);
  const [loading, setLoading]                 = useState(false);

  // Refresh on mount in case context already loaded stale data
  React.useEffect(() => {
    setLoading(true);
    loadCustomPages().finally(() => setLoading(false));
  }, []);

  // ── Real data computations ──────────────────────────────────
  const publishedCount = useMemo(() => customPages.filter(p => p.status === 'published').length, [customPages]);
  const draftCount     = useMemo(() => customPages.filter(p => p.status !== 'published').length, [customPages]);
  const totalSections  = useMemo(() => customPages.reduce((s, p) => s + (p.sections?.length || 0), 0), [customPages]);

  const monthlyOrderData = useMemo(() => {
    const map = {};
    const getAmount = o => Number(o.amount) || (o.items || []).reduce((s, i) => s + Number(i.price) * Number(i.quantity), 0);
    orders.forEach(o => {
      if (!o.date) return;
      const d   = new Date(o.date);
      const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
      if (!map[key]) map[key] = { month: MONTHS_SHORT[d.getMonth()], _sort: d.getFullYear() * 100 + d.getMonth(), orders: 0, revenue: 0 };
      map[key].orders++;
      map[key].revenue += getAmount(o);
    });
    return Object.values(map).sort((a, b) => a._sort - b._sort).slice(-6);
  }, [orders]);

  const sectionTypeData = useMemo(() => {
    const map = {};
    customPages.forEach(p => {
      (p.sections || []).forEach(s => {
        const type = (s.type || 'unknown').replace(/-/g, ' ');
        map[type] = (map[type] || 0) + 1;
      });
    });
    return Object.entries(map).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 8);
  }, [customPages]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addSection = type => setForm(f => ({
    ...f,
    sections: [...f.sections, { id: Date.now(), type, content: { ...({ ...SECTION_DEFAULTS, ...EXTRA_DEFAULTS })[type] } }],
  }));

  const updateSection = (id, content) =>
    setForm(f => ({ ...f, sections: f.sections.map(s => s.id === id ? { ...s, content } : s) }));

  const deleteSection = id =>
    setForm(f => ({ ...f, sections: f.sections.filter(s => s.id !== id) }));

  const moveSection = (id, direction) =>
    setForm(f => {
      const idx = f.sections.findIndex(s => s.id === id);
      if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === f.sections.length - 1)) return f;
      const arr = [...f.sections];
      const ti = direction === 'up' ? idx - 1 : idx + 1;
      [arr[idx], arr[ti]] = [arr[ti], arr[idx]];
      return { ...f, sections: arr };
    });

  const save = async () => {
    if (!form.title || !form.slug) return;
    setSaving(true);
    try {
      await saveCustomPage(form, editing);
      setShowEditor(false); setForm(BLANK); setPreviewMode(false);
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async (id) => {
    await deleteCustomPage(id);
    setConfirmDeleteId(null);
  };
  const openNew  = () => { setForm(BLANK); setEditing(null); setShowEditor(true); setPreviewMode(false); };
  const openEdit = p  => {
    setForm({
      title:     p.title     || '',
      slug:      p.slug      || '',
      navTitle:  p.navTitle  || p.nav_title  || '',
      showInNav: p.showInNav !== undefined ? p.showInNav : (p.show_in_nav ?? true),
      status:    p.status    || 'draft',
      sections:  Array.isArray(p.sections) ? p.sections : [],
    });
    setEditing(p.id);
    setShowEditor(true);
    setPreviewMode(false);
  };

  const filteredBtns = SECTION_BTNS.filter(([label]) =>
    label.toLowerCase().includes(sectionSearch.toLowerCase())
  );

      const StatCard = ({ icon, label, value, sub, color, positive }) => (
  <div className="bg-white rounded-2xl p-5 flex items-center gap-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0 ${color}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide truncate">{label}</p>
      <p className="text-2xl font-bold text-gray-800 leading-tight">{value}</p>
      {sub && (
        <p className={`text-xs mt-0.5 font-semibold ${positive === true ? "text-emerald-500" : positive === false ? "text-red-500" : "text-gray-400"}`}>
          {sub}
        </p>
      )}
    </div>
  </div>
);

  return (
    <div className="max-w-7xl mx-auto  ">
      <div className='flex gap-2 flex-col'>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon="📄" label="Total Pages"     value={customPages.length}  sub={`${publishedCount} published, ${draftCount} draft`} color="bg-indigo-500" />
          <StatCard icon="✓"  label="Published"       value={publishedCount}      sub="Live on site"  positive={publishedCount > 0}         color="bg-emerald-500" />
          <StatCard icon="📝" label="Draft"           value={draftCount}          sub="Not yet live"                                        color="bg-violet-500" />
          <StatCard icon="⬜" label="Total Sections"  value={totalSections}       sub="Across all pages"                                    color="bg-amber-500" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SectionCard title="Monthly Orders" subtitle="From real order data">
            {monthlyOrderData.length === 0 ? (
              <div className="flex items-center justify-center h-60 text-gray-400 text-sm">No order data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={monthlyOrderData}>
                  <defs>
                    <linearGradient id="ordG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/><stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/></linearGradient>
                    <linearGradient id="revG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip /><Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Area yAxisId="l" type="monotone" dataKey="orders"  stroke="#8b5cf6" strokeWidth={2.5} fill="url(#ordG)" name="Orders" />
                  <Area yAxisId="r" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2.5} fill="url(#revG)" name="Revenue ($)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
          <SectionCard title="Section Types Used" subtitle="Across all custom pages">
            {sectionTypeData.length === 0 ? (
              <div className="flex items-center justify-center h-60 text-gray-400 text-sm">No sections yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={sectionTypeData} layout="vertical" margin={{ left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} name="Uses" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        </div>
      </div>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Pages</h1>
          <p className="text-slate-500 mt-1">{customPages.length} custom {customPages.length === 1 ? 'page' : 'pages'}</p>
        </div>
        <button onClick={openNew}
          className="rounded-xl bg-indigo-600 text-white font-semibold px-5 py-2.5 text-sm hover:bg-indigo-700 transition shadow-md">
          + Create Page
        </button>
      </div>

      {/* Page cards / empty state */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-20 text-center text-slate-400">
          <div className="text-4xl mb-3 animate-pulse">📄</div>
          <p className="text-sm">Loading pages…</p>
        </div>
      ) : customPages.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-20 text-center">
          <div className="text-6xl mb-4">📄</div>
          <h3 className="text-2xl font-bold text-slate-800 mb-2">No custom pages yet</h3>
          <p className="text-slate-500 mb-6">Create landing pages, about pages, or any custom content</p>
          <button onClick={openNew}
            className="rounded-xl bg-indigo-600 text-white font-semibold px-6 py-2.5 text-sm hover:bg-indigo-700 transition">
            Create Your First Page
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-5">
          {customPages.map(p => {
            const sections = Array.isArray(p.sections) ? p.sections : [];
            const inNav = p.showInNav !== undefined ? p.showInNav : p.show_in_nav;
            return (
            <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-xl font-bold text-slate-900 mb-1">{p.title}</div>
                  <div className="text-xs text-slate-400 mb-2">/{p.slug}</div>
                  {inNav && (
                    <span className="inline-block rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold px-2.5 py-0.5">In Navigation</span>
                  )}
                </div>
                <span className={`inline-block rounded-full text-[11px] font-semibold px-2.5 py-0.5 ${p.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {p.status}
                </span>
              </div>
              <p className="text-xs text-slate-400 mb-4">{sections.length} section{sections.length !== 1 ? 's' : ''}</p>
              <div className="flex gap-2">
                <button onClick={() => openEdit(p)}
                  className="flex-1 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 text-sm font-medium py-2 hover:bg-slate-100 transition">
                  Edit
                </button>
                {p.status === 'published' && (
                  <a href={`/${p.slug}`} target="_blank" rel="noreferrer"
                    className="rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-600 text-sm font-medium px-4 py-2 hover:bg-indigo-100 transition">
                    View ↗
                  </a>
                )}
                {confirmDeleteId === p.id ? (
                  <div className="flex gap-1">
                    <button onClick={() => deletePage(p.id)}
                      className="rounded-lg bg-red-600 text-white text-xs font-semibold px-3 py-2 hover:bg-red-700 transition">Confirm</button>
                    <button onClick={() => setConfirmDeleteId(null)}
                      className="rounded-lg border border-slate-200 bg-slate-50 text-slate-600 text-xs font-semibold px-3 py-2 hover:bg-slate-100 transition">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmDeleteId(p.id)}
                    className="rounded-lg border border-red-200 bg-red-50 text-red-500 text-sm font-medium px-4 py-2 hover:bg-red-100 transition">Delete</button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* ── EDITOR MODAL ── */}
      {showEditor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/70 p-5 backdrop-blur-sm">
          <div className={`w-full my-5 rounded-2xl bg-white shadow-2xl transition-all ${previewMode ? 'max-w-5xl' : 'max-w-3xl'}`}>

            {/* Modal header */}
            <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl border-b border-slate-200 bg-white px-7 py-4">
              <h2 className="text-2xl font-bold text-slate-900">{editing ? 'Edit Page' : 'Create Page'}</h2>
              <div className="flex items-center gap-3">
                <button onClick={() => setPreviewMode(!previewMode)}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 transition">
                  {previewMode ? '📝 Edit' : '👁️ Preview'}
                </button>
                {editing && form.slug && form.status === 'published' && (
                  <a href={`/${form.slug}`} target="_blank" rel="noreferrer"
                    className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-100 transition">
                    View Live ↗
                  </a>
                )}
                <button onClick={() => { setShowEditor(false); setPreviewMode(false); }}
                  className="text-2xl text-slate-400 hover:text-slate-700 transition leading-none">✕</button>
              </div>
            </div>

            {!previewMode ? (
              <div className="p-8">
                {/* Page settings */}
                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-6 mb-8">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-5">Page Settings</p>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {[['Page Title *','title','About Us',false],['URL Slug *','slug','about-us',true],['Navigation Label','navTitle','About',false]].map(([label,key,placeholder,isSlug]) => (
                      <label key={key} className="flex flex-col gap-1">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
                        <input
                          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-indigo-400 transition"
                          value={form[key]}
                          placeholder={placeholder}
                          onChange={e => update(key, isSlug ? e.target.value.toLowerCase().replace(/\s+/g, '-') : e.target.value)}
                        />
                      </label>
                    ))}
                    <label className="flex flex-col gap-1">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</span>
                      <select className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-indigo-400 transition"
                        value={form.status} onChange={e => update('status', e.target.value)}>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </label>
                  </div>
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input type="checkbox" checked={form.showInNav} onChange={e => update('showInNav', e.target.checked)} className="w-4 h-4 accent-indigo-600" />
                    <span className="text-sm text-slate-700">Show in navigation menu</span>
                  </label>
                </div>

                {/* Section builder */}
                <div className="mb-8">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
                    Page Sections ({form.sections.length})
                  </p>

                  {/* Searchable section picker */}
                  <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 mb-4">
                    <input
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400 mb-3"
                      placeholder="🔍 Search sections…"
                      value={sectionSearch}
                      onChange={e => setSectionSearch(e.target.value)}
                    />
                    <div className="flex flex-wrap gap-2">
                      {filteredBtns.map(([label, type]) => (
                        <button key={type} onClick={() => addSection(type)}
                          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-600 transition">
                          + {label}
                        </button>
                      ))}
                      {filteredBtns.length === 0 && (
                        <p className="text-xs text-slate-400 italic">No sections match your search.</p>
                      )}
                    </div>
                  </div>

                  {form.sections.length === 0 ? (
                    <div className="rounded-2xl border-2 border-dashed border-slate-200 p-16 text-center text-slate-400">
                      <div className="text-5xl mb-3">📄</div>
                      <p className="text-sm">No sections yet. Click a button above to add your first section.</p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      {form.sections.map((section, idx) => (
                        <SectionEditor key={section.id} section={section}
                          updateSection={updateSection} deleteSection={deleteSection} moveSection={moveSection}
                          isFirst={idx === 0} isLast={idx === form.sections.length - 1} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Save / cancel */}
                <div className="flex gap-3 pt-6 border-t border-slate-200">
                  <button onClick={() => { setShowEditor(false); setPreviewMode(false); }}
                    className="flex-1 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 font-semibold py-3 hover:bg-slate-100 transition">Cancel</button>
                  <button onClick={save} disabled={!form.title || !form.slug || saving}
                    className="flex-[2] rounded-xl bg-indigo-600 text-white font-semibold py-3 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-md">
                    {saving ? 'Saving…' : editing ? 'Update Page' : 'Create Page'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="min-h-96 overflow-hidden rounded-b-2xl">
                <CustomPageRenderer page={form} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}